import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    FiArrowLeft, FiSave, FiX, FiUser, FiCalendar,
    FiHome, FiLayers, FiTool, FiFileText, FiTrash2,
    FiPlus, FiCheck, FiAlertCircle, FiChevronDown, FiChevronRight, FiEdit2
} from "react-icons/fi";

import { FiImage, FiFile, FiFilePdf } from "react-icons/fi";
import { TbMeterSquare } from "react-icons/tb";
import { Code, FileArchive, FileAudio, FileSpreadsheet, FileVideo, Grid2X2, Info, Receipt, Timer, Truck } from "lucide-react";
import { FaRegFileWord, FaRegFilePdf } from "react-icons/fa6";

import { useAuth } from "../../contexts/AuthContext";
import {
    updateAssignment,
    updateJobQuotes,
    updateJobFloors,
    updateJobFloorSpecs,
    deleteJobFiles,
    logJobAction,
    createChildQuote,
    setChildQuoteRequired,
    syncJobQuotesAfterEdit,
    updateChildQuotesDates,
    updateSingleQuoteDate,
    updateQuoteInvoiceState,
    updateQuoteInvoiced,
    updateQuoteDeliveryField,
    updateChildUniqueId,
    isChildUniqueIdTaken,
} from "../../lib/supabase/edit-assignment";

import {
    getSingleAssignment,
} from "../../lib/supabase/single-assignment";
import { updateSiteMeasureDate } from "../../lib/supabase/schedule";
import { createJobActionLog } from "../../lib/supabase/activityLog";

import DragDropUploader from "../common/DragDropUploader"
import { uploadJobFiles } from "../../lib/supabase/assignments";
import DropdownSection from "../common/DropdownSection";
import { format } from "date-fns";

import ConfirmationPopup from "../common/ConfirmationPopup";
import FileSearchBar from "../common/FileSearchBar";

import { GiWoodBeam } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";

import {
    getEnabledExtraQuoteTypes,
    getQuoteLabel,
    QUOTE_ORDER as SHARED_QUOTE_ORDER,
} from "../../lib/quoteTypes";
import { supabase } from "../../config/supabase";

const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-zinc-700">
            <Icon className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-white">{title}</h3>
        </div>
        <div className="space-y-2">
            {children}
        </div>
    </div>
);

const CheckboxButton = ({ label, isChecked, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm flex items-center gap-2 ${isChecked
            ? 'bg-green-500 text-white border-green-500 shadow-md'
            : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
            } ${className}`}
    >
        {isChecked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
        {label}
    </button>
);

const SelectionButton = ({ label, isSelected, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium ${isSelected
            ? 'bg-gray-800 text-white border-gray-800 shadow-md'
            : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700'
            } ${className}`}
    >
        {label}
    </button>
);

const EditAssignment = ({ modalJobId, modalCompanySlug, onClose } = {}) => {
    const { userProfile, company } = useAuth();
    const { companySlug: paramsCompanySlug, jobId: paramsJobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isModal = !!onClose;
    const jobId = modalJobId ?? paramsJobId;
    const companySlug = modalCompanySlug ?? paramsCompanySlug;

    const [clearPdfs, setClearPdfs] = useState(false);


    const [formData, setFormData] = useState({
        uniqueId: "",
        customerName: "",
        customerNumber: "",
        customerMail: "",
        carpenterSupervisorName: "",
        carpenterSupervisorNumber: "",
        customerAddress: "",
        jobType: "Design & Estimation",
        quotes: {
            roof: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
            wall: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
            floor: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
            timber: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
            beams: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
            flooring_sheets: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "", invoiced: false },
        },
        roofType: "",
        roofTrussCentres: "",
        isSingleStorey: true,
        isTripleStorey: false,
        singleStorey: {
            nonLoadBearingWallStudSizes: "90x45",
            nonLoadBearingWallStudCentres: "",
            wallHeight: ""
        },
        doubleStorey: {
            groundFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            firstFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            }
        },
        tripleStorey: {
            groundFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            firstFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            secondFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            }
        },
        floors: [{
            floorNumber: 1,
            floorTrussSize: "",
            flooring: "",
            balconyIncluded: false
        }],
        siteMeasureRequired: false,
        siteMeasureWall: false,
        siteMeasureFloor: false,
        siteMeasureTruss: false,
        isUnit: false,
        unitCount: "",
        urgentQuote: false,
        urgentDesign: false,
        invoiced: false,
        expectedEstimationFinishDate: ""
    });

    const [originalAssignment, setOriginalAssignment] = useState(null);
    const [newFiles, setNewFiles] = useState([]);
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [fileSearch, setFileSearch] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    // Per-quote-row date overrides for the Dates section (keyed by quote row id)
    const [quoteDates, setQuoteDates] = useState({});

    // Per-quote-row invoice state overrides (keyed by quote row id)
    const [quoteInvoiceStates, setQuoteInvoiceStates] = useState({});

    // Per-quote-row invoiced boolean (keyed by quote row id)
    const [quoteInvoicedMap, setQuoteInvoicedMap] = useState({});

    // Per-quote-row delivery fields (keyed by quote row id)
    const [quoteTimeSlots, setQuoteTimeSlots] = useState({});
    const [quoteDeliveryTags, setQuoteDeliveryTags] = useState({});
    const [quoteDeliveryNotes, setQuoteDeliveryNotes] = useState({});
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteValue, setEditingNoteValue] = useState('');

    // Child departments state
    const [childDepts, setChildDepts] = useState([]);
    const [newChildRow, setNewChildRow] = useState(null); // null = hidden, object = visible form
    const [childActionLoading, setChildActionLoading] = useState(null); // quoteId of in-progress toggle
    const [newChildAddLoading, setNewChildAddLoading] = useState(false);
    const [disabledChildrenOpen, setDisabledChildrenOpen] = useState(false);
    const [showAllChildren, setShowAllChildren] = useState(false);
    const [disableChildPopup, setDisableChildPopup] = useState(null);

    const [quoteDateWarningPopup, setQuoteDateWarningPopup] = useState(null);
    const [showQuoteDateWarningDetails, setShowQuoteDateWarningDetails] = useState(false);

    // Inline child_unique_id editor
    const [editingChildId, setEditingChildId] = useState(null); // quoteId being edited
    const [editingChildUniqueId, setEditingChildUniqueId] = useState('');
    const [childIdError, setChildIdError] = useState('');
    const [childIdSaving, setChildIdSaving] = useState(false);

    // Site measure date popup: { quoteType, formKey, quoteId, label, date, isEdit }
    const [smPopup, setSmPopup] = useState(null);
    const [smSaving, setSmSaving] = useState(false);

    const enabledExtraQuoteTypes = useMemo(() => {
        return getEnabledExtraQuoteTypes(company);
    }, [company?.settings]);

    const hasExtraQuoteTypes = enabledExtraQuoteTypes.length > 0;

    useEffect(() => {
        loadAssignment();
    }, [jobId, company?.id]);

    const loadAssignment = async () => {
        try {
            setLoading(true);
            const assignment = await getSingleAssignment(jobId, company.id);
            setOriginalAssignment(assignment);

            // Map assignment data to form structure.
            //
            // isRequired (the department checkbox) comes from two sources depending on job era:
            //   - New-system jobs: `selected_departments` array on the job root is authoritative.
            //     These jobs have only child quote rows (child_unique_id set); there are no
            //     parent-level rows to derive isRequired from.
            //   - Old-system jobs: `selected_departments` is null — fall back to parent-level
            //     quote row's is_required flag.
            //
            // Dates / invoiced always come from the parent-level quote row (child_unique_id IS
            // NULL) when one exists; otherwise they default to empty.
            const selectedDepts = new Set(assignment.selected_departments || []);
            // New-system jobs: selected_departments has entries (e.g. ["floor","roof","wall"])
            // Old-system jobs: selected_departments is an empty array — use parent quote rows instead
            const useSelectedDepts = selectedDepts.size > 0;

            // Index parent-level quote rows by type for date/invoiced lookup
            // (old-system: one parent row per type with child_unique_id = null)
            const parentQuoteMap = {};
            (assignment.job_quotes || [])
                .filter(q =>
                    !q.child_unique_id &&
                    (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined)
                )
                .forEach(q => { parentQuoteMap[q.quote_type] = q; });

            // For new-system jobs (no parent rows), fall back to the first child quote
            // per type to pre-populate date fields so existing dates are shown/preserved.
            const childDateMap = {};
            if (useSelectedDepts) {
                (assignment.job_quotes || [])
                    .filter(q =>
                        q.child_unique_id &&
                        (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined)
                    )
                    .forEach(q => {
                        if (!childDateMap[q.quote_type]) childDateMap[q.quote_type] = q;
                    });
            }

            const quotes = {};
            ['roof', 'wall', 'floor', 'timber', ...enabledExtraQuoteTypes].forEach(type => {
                const pq = parentQuoteMap[type];
                // Use parent row for dates if available; else first child row (new-system)
                const dateSrc = pq || childDateMap[type];
                quotes[type] = {
                    isRequired: useSelectedDepts
                        ? selectedDepts.has(type)          // new-system: use selected_departments
                        : (pq?.is_required || false),      // old-system: use parent quote row
                    expectedDeliveryDate: dateSrc?.expected_delivery_date
                        ? new Date(dateSrc.expected_delivery_date).toISOString().slice(0, 10) : "",
                    expectedFinishDate: dateSrc?.expected_finish_date
                        ? new Date(dateSrc.expected_finish_date).toISOString().slice(0, 10) : "",
                    invoiced: pq?.invoiced || false,
                };
            });

            // Get floor specs
            const singleStoreySpec = assignment.job_floor_specs?.find(s => s.floor_level === 'single_storey');
            const groundFloorSpec = assignment.job_floor_specs?.find(s => s.floor_level === 'ground');
            const firstFloorSpec = assignment.job_floor_specs?.find(s => s.floor_level === 'first');
            const secondFloorSpec = assignment.job_floor_specs?.find(s => s.floor_level === 'second');

            setFormData({
                uniqueId: assignment.unique_id || "",
                customerName: assignment.customer_name || "",
                customerNumber: normaliseAuPhone(assignment.customer_number || ""),
                customerMail: assignment.customer_mail || "",
                carpenterSupervisorName: assignment.carpenter_supervisor_name || "",
                carpenterSupervisorNumber: normaliseAuPhone(assignment.carpenter_supervisor_number || ""),
                customerAddress: assignment.customer_address || "",
                jobType: assignment.job_type || "Design & Estimation",
                quotes: quotes,
                roofType: assignment.roof_type || "",
                roofTrussCentres: assignment.roof_truss_centres || "",
                isSingleStorey: assignment.is_single_storey !== false,
                isTripleStorey: assignment.storey === 'triple',
                singleStorey: {
                    nonLoadBearingWallStudSizes: singleStoreySpec?.non_load_bearing_wall_stud_sizes || "90x45",
                    nonLoadBearingWallStudCentres: singleStoreySpec?.non_load_bearing_wall_stud_centres || "",
                    wallHeight: singleStoreySpec?.wall_height || ""
                },
                doubleStorey: {
                    groundFloor: {
                        nonLoadBearingWallStudSizes: groundFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: groundFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: groundFloorSpec?.wall_height || ""
                    },
                    firstFloor: {
                        nonLoadBearingWallStudSizes: firstFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: firstFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: firstFloorSpec?.wall_height || ""
                    }
                },
                tripleStorey: {
                    groundFloor: {
                        nonLoadBearingWallStudSizes: groundFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: groundFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: groundFloorSpec?.wall_height || ""
                    },
                    firstFloor: {
                        nonLoadBearingWallStudSizes: firstFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: firstFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: firstFloorSpec?.wall_height || ""
                    },
                    secondFloor: {
                        nonLoadBearingWallStudSizes: secondFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: secondFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: secondFloorSpec?.wall_height || ""
                    }
                },
                floors: assignment.job_floors?.map(f => ({
                    floorNumber: f.floor_number,
                    floorTrussSize: f.floor_truss_size || "",
                    flooring: f.flooring || "",
                    balconyIncluded: f.balcony_included || false
                })) || [{
                    floorNumber: 1,
                    floorTrussSize: "",
                    flooring: "",
                    balconyIncluded: false
                }],
                siteMeasureRequired: assignment.site_measure_required || false,
                siteMeasureWall: assignment.site_measure_wall || false,
                siteMeasureFloor: assignment.site_measure_floor || false,
                siteMeasureTruss: assignment.site_measure_truss || false,
                isUnit: assignment.is_unit || false,
                unitCount: assignment.unit_count?.toString() || "",
                urgentQuote: assignment.urgent_quote || false,
                urgentDesign: assignment.urgent_design || false,
                invoiced: assignment.invoiced || false,
                expectedEstimationFinishDate: assignment.expected_estimation_finish_date ?
                    new Date(assignment.expected_estimation_finish_date).toISOString().slice(0, 16) : ""
            });
            // Show ALL non-redelivery quotes as child department rows.
            // This includes old-system parent rows (no child_unique_id) alongside
            // new-system child rows so that everything remains visible even after
            // the job is upgraded by adding the first child department.
            const childQuotes = (assignment.job_quotes || [])
                .filter(q => (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined));
            setChildDepts(childQuotes);

            // Seed invoice states from loaded job_quotes rows
            const invoiceStateMap = {};
            const invoicedBoolMap = {};
            const timeSlotsMap = {};
            const deliveryTagsMap = {};
            const deliveryNotesMap = {};
            (assignment.job_quotes || []).forEach(q => {
                invoiceStateMap[q.id] = q.invoice_state || 'not_invoiced';
                invoicedBoolMap[q.id] = q.invoiced || false;
                timeSlotsMap[q.id] = q.delivery_time_slot || '';
                deliveryTagsMap[q.id] = q.delivery_tag || '';
                deliveryNotesMap[q.id] = q.delivery_note || '';
            });
            setQuoteInvoiceStates(invoiceStateMap);
            setQuoteInvoicedMap(invoicedBoolMap);
            setQuoteTimeSlots(timeSlotsMap);
            setQuoteDeliveryTags(deliveryTagsMap);
            setQuoteDeliveryNotes(deliveryNotesMap);

        } catch (error) {
            console.error("Error loading assignment:", error);
            alert("Error loading assignment details");
            if (isModal) { onClose(); } else { navigate(`/${companySlug}/assignment/list`); }
        } finally {
            setLoading(false);
        }
    };

    const normaliseAuPhone = (value) => {
        if (!value) return "";

        let digits = String(value).replace(/\D/g, "");

        // Convert 0061XXXXXXXXX → 61XXXXXXXXX
        if (digits.startsWith("0061")) {
            digits = digits.slice(2);
        }

        // Convert +61XXXXXXXXX / 61XXXXXXXXX → local mobile
        if (digits.startsWith("61")) {
            digits = digits.slice(2);
        }

        // Convert 04XXXXXXXX → 4XXXXXXXX
        if (digits.startsWith("0")) {
            digits = digits.slice(1);
        }

        // Keep only first 9 digits
        return digits.slice(0, 9);
    };

    const formatPhoneDisplay = (value) => {
        const d = normaliseAuPhone(value);

        if (d.length <= 3) return d;
        if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
        return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    };

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        if (name === "customerNumber" || name === "carpenterSupervisorNumber") {
            const phone = normaliseAuPhone(value);

            setFormData(prevState => ({
                ...prevState,
                [name]: phone
            }));
            return;
        }

        if (name.includes('.')) {
            handleNestedChange(name, type === 'checkbox' ? checked : value);
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const handleNestedChange = useCallback((path, value) => {
        const keys = path.split('.');
        setFormData(prevState => {
            const newState = { ...prevState };
            let current = newState;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    }, []);

    // Helper function to toggle selection - if already selected, reset to empty/initial value
    const handleToggleSelection = useCallback((path, value, initialValue = "") => {
        const keys = path.split('.');
        let current = formData;

        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) return;
            current = current[keys[i]];
        }

        const currentValue = current[keys[keys.length - 1]];
        const newValue = currentValue === value ? initialValue : value;

        handleNestedChange(path, newValue);
    }, [formData, handleNestedChange]);

    const renderWallSpecs = (prefix, data, title) => (
        <div className="bg-gray-50 dark:bg-zinc-900/50 border border-gray-200 dark:border-zinc-700 p-4 rounded-lg space-y-4 mt-4">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{title}</h4>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Non Load Bearing Wall Stud Sizes
                </p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="90x45"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x45"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudSizes`, "90x45")}
                    />
                    <SelectionButton
                        label="90x35"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x35"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudSizes`, "90x35")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Non Load Bearing Wall Stud Centres
                </p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="450"
                        isSelected={data.nonLoadBearingWallStudCentres === "450"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudCentres`, "450")}
                    />
                    <SelectionButton
                        label="600"
                        isSelected={data.nonLoadBearingWallStudCentres === "600"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudCentres`, "600")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Wall Height</p>
                <input
                    className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    value={data.wallHeight}
                    onChange={(e) => handleNestedChange(`${prefix}.wallHeight`, e.target.value)}
                    placeholder="Wall Height (general without stepdown)"
                />
            </div>
        </div>
    );

    const handleFloorChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            floors: prev.floors.map((floor, i) =>
                i === index ? { ...floor, [field]: value } : floor
            )
        }));
    };

    const getAvailableDepartments = () => {

        let availableDeps = [];
        const deps = Object.keys(formData?.quotes);

        deps.forEach(department => {
            let departmentValues = formData?.quotes?.[department];
            if (departmentValues?.isRequired) {
                availableDeps.push(`${department}`)
            }
        })

        return availableDeps;
    }

    const getAvailableCategories = () => {

        let availableCategories = ["initial"];

        let jobType = formData?.jobType;
        if (jobType?.toLowerCase()?.includes("estimation")) availableCategories.push("estimation");
        if (jobType?.toLowerCase()?.includes("design")) availableCategories.push("design");
        if (jobType?.toLowerCase()?.includes("timber")) availableCategories.push("timber");

        return availableCategories;
    }

    const addFloor = () => {
        setFormData(prev => ({
            ...prev,
            floors: [...prev.floors, {
                floorNumber: prev.floors.length + 1,
                floorTrussSize: "",
                flooring: "",
                balconyIncluded: false
            }]
        }));
    };

    const removeFloor = (index) => {
        if (formData.floors.length === 1) return;

        setFormData(prev => ({
            ...prev,
            floors: prev.floors.filter((_, i) => i !== index)
                .map((floor, i) => ({ ...floor, floorNumber: i + 1 }))
        }));
    };

    // ── Child department handlers ─────────────────────────────────────────────
    const QUOTE_LABELS = {
        roof: 'Roof',
        wall: 'Wall',
        floor: 'Floor',
        timber: 'Timber',
        beams: 'Beams',
        flooring_sheets: 'Flooring Sheets',
    };

    const groupRowsByQuoteType = (rows = []) => {
        return rows.reduce((acc, row) => {
            const type = row?.quote_type || 'other';
            if (!acc[type]) acc[type] = [];
            acc[type].push(row);
            return acc;
        }, {});
    };

    const getSortedQuoteTypeEntries = (rows = []) => {
        const grouped = groupRowsByQuoteType(rows);

        return Object.entries(grouped)
            .sort(([a], [b]) => (QUOTE_ORDER[a] ?? 99) - (QUOTE_ORDER[b] ?? 99))
            .map(([quoteType, items]) => ({
                quoteType,
                label: getQuoteLabel(quoteType),
                items: items.sort((a, b) => {
                    return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                }),
            }));
    };

    const QUOTE_ORDER = SHARED_QUOTE_ORDER;

    const getDesignStatusBadge = (status) => {
        if (!status) return null;
        const map = {
            pending_design: 'bg-gray-100 text-gray-500 border-gray-200',
            waiting_assignment: 'bg-gray-100 text-gray-500 border-gray-200',
            in_design: 'bg-amber-100 text-amber-700 border-amber-200',
            in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
            design_completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            pending_estimation: 'bg-orange-100 text-orange-700 border-orange-200',
            in_estimation: 'bg-amber-100 text-amber-700 border-amber-200',
            estimation_completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            ready_for_delivery: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            on_hold: 'bg-red-100 text-red-600 border-red-200',
        };
        const cls = map[status] || 'bg-gray-100 text-gray-500 border-gray-200';
        const label = status.replaceAll('_', ' ');
        return (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none flex-shrink-0 ${cls}`}>
                {label}
            </span>
        );
    };

    const getChildQuoteIcon = (quoteType) => {
        if (quoteType === 'roof') return <FiHome />;
        if (quoteType === 'wall') return <FiLayers />;
        if (quoteType === 'floor') return <Grid2X2 />;
        if (quoteType === 'beams') return <GiWoodBeam />;
        if (quoteType === 'flooring_sheets') return <HiOutlineSquare3Stack3D />;
        return <FiLayers />;
    };

    const getQuoteTypeIconComponent = (quoteType) => {
        if (quoteType === 'roof') return FiHome;
        if (quoteType === 'wall') return FiLayers;
        if (quoteType === 'floor') return Grid2X2;
        if (quoteType === 'timber') return Truck;
        if (quoteType === 'beams') return GiWoodBeam;
        if (quoteType === 'flooring_sheets') return HiOutlineSquare3Stack3D;
        return FiLayers;
    };

    const openNewChildRow = () => {
        const defaultType = originalAssignment?.selected_departments?.[0] || 'roof';
        setNewChildRow({ quoteType: defaultType, childUniqueId: '', expectedFinishDate: '', expectedDeliveryDate: '' });
    };

    // Helper: find quote_type label for a given quote row id
    const quoteTypeById = (quoteId) => {
        const q = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
        return q?.quote_type || null;
    };
    const logAction = (params) => createJobActionLog({ ...params, action_origin: 'edit-assignment' }).catch(() => { });
    const logBase = () => ({ job_id: originalAssignment?.id, user_id: userProfile?.id, user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown', user_role: userProfile?.role });

    const getActiveChildDepartmentsCount = () => {
        return childDepts.filter(d => d.is_required).length;
    };

    const actuallyToggleChildRequired = async (quoteId, currentIsRequired) => {
        setChildActionLoading(quoteId);
        try {
            await setChildQuoteRequired(quoteId, !currentIsRequired);
            setChildDepts(prev =>
                prev.map(d => d.id === quoteId ? { ...d, is_required: !currentIsRequired } : d)
            );

            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;

            logAction({
                ...logBase(),
                action: 'toggle_required',
                quote_type: qt,
                details: `${!currentIsRequired ? 'Enabled' : 'Disabled'} required on #${label}`
            });
        } catch (err) {
            console.error(err);
            alert('Failed to update department status. Please try again.');
        } finally {
            setChildActionLoading(null);
        }
    };

    const handleToggleChildRequired = async (quoteId, currentIsRequired) => {
        // Enabling does not need warning
        if (!currentIsRequired) {
            await actuallyToggleChildRequired(quoteId, currentIsRequired);
            return;
        }

        const activeCount = getActiveChildDepartmentsCount();

        // If this is the only enabled child department left, warn first
        if (activeCount <= 1) {
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;

            setDisableChildPopup({
                quoteId,
                currentIsRequired,
                childLabel: label,
            });
            return;
        }

        await actuallyToggleChildRequired(quoteId, currentIsRequired);
    };

    const handleConfirmDisableLastChild = async () => {
        if (!disableChildPopup) return;

        const { quoteId, currentIsRequired } = disableChildPopup;
        setDisableChildPopup(null);

        await actuallyToggleChildRequired(quoteId, currentIsRequired);
    };

    const handleCancelDisableLastChild = () => {
        setDisableChildPopup(null);
    };

    // const handleToggleChildRequired = async (quoteId, currentIsRequired) => {
    //     setChildActionLoading(quoteId);
    //     try {
    //         await setChildQuoteRequired(quoteId, !currentIsRequired);
    //         setChildDepts(prev => prev.map(d => d.id === quoteId ? { ...d, is_required: !currentIsRequired } : d));
    //         const qt = quoteTypeById(quoteId);
    //         const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
    //         const label = row?.child_unique_id || qt || quoteId;
    //         logAction({ ...logBase(), action: 'toggle_required', quote_type: qt, details: `${!currentIsRequired ? 'Enabled' : 'Disabled'} required on #${label}` });
    //     } catch (err) {
    //         console.error(err);
    //         alert('Failed to update department status. Please try again.');
    //     } finally {
    //         setChildActionLoading(null);
    //     }
    // };

    const handleAddChildDept = async () => {
        if (!newChildRow?.childUniqueId?.trim() || !newChildRow?.quoteType) return;
        setNewChildAddLoading(true);
        try {
            const created = await createChildQuote(originalAssignment.id, {
                quoteType: newChildRow.quoteType,
                childUniqueId: newChildRow.childUniqueId.trim(),
                expectedFinishDate: newChildRow.expectedFinishDate || null,
                expectedDeliveryDate: newChildRow.expectedDeliveryDate || null,
            });
            setChildDepts(prev => [...prev, created]);
            setNewChildRow(null);
            logAction({ ...logBase(), action: 'add_child_dept', quote_type: newChildRow.quoteType, details: `Added child dept #${newChildRow.childUniqueId.trim()} (${newChildRow.quoteType})` });
        } catch (err) {
            console.error(err);
            alert('Failed to add department. Please try again.');
        } finally {
            setNewChildAddLoading(false);
        }
    };

    const handleSaveChildUniqueId = async (quoteId) => {
        const trimmed = editingChildUniqueId.trim();
        if (!trimmed) return;

        // Local duplicate check — case-insensitive
        const trimmedLower = trimmed.toLowerCase();
        const localDupe = childDepts.some(
            c => c.id !== quoteId && (c.child_unique_id || '').toLowerCase() === trimmedLower
        );
        if (localDupe) {
            setChildIdError('This ID is already used in this job');
            return;
        }

        setChildIdSaving(true);
        setChildIdError('');
        try {
            const taken = await isChildUniqueIdTaken(trimmed, quoteId);
            if (taken) {
                setChildIdError('This ID already exists in the system');
                return;
            }
            await updateChildUniqueId(quoteId, trimmed);
            const qt = quoteTypeById(quoteId);
            const oldRow = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            logAction({ ...logBase(), action: 'update_child_unique_id', quote_type: qt, details: `Changed ID from #${oldRow?.child_unique_id || '?'} to #${trimmed}` });

            // Patch childDepts so the Department list updates
            setChildDepts(prev => prev.map(c => c.id === quoteId ? { ...c, child_unique_id: trimmed } : c));

            // Also patch originalAssignment.job_quotes so ETA Dates, Invoices, and any
            // other section reading from that source reflects the new ID immediately
            setOriginalAssignment(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    job_quotes: (prev.job_quotes || []).map(q =>
                        q.id === quoteId ? { ...q, child_unique_id: trimmed } : q
                    ),
                };
            });

            setEditingChildId(null);
        } catch (err) {
            console.error(err);
            setChildIdError('Failed to update ID. Please try again.');
        } finally {
            setChildIdSaving(false);
        }
    };

    const DEPARTMENT_ORDER = {
        wall: 0,
        floor: 1,
        roof: 2,
    };

    const getQuoteDateForWarning = (quote, overrides = {}) => {
        return (
            overrides[quote.id] ||
            quote?.expected_delivery_date ||
            quote?.expected_finish_date ||
            null
        );
    };

    const toDayTime = (value) => {
        if (!value) return null;

        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        ).getTime();
    };

    const formatWarningDate = (date) => {
        if (!date) return 'No date';

        return new Date(date).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getEtaDateOrderWarnings = (quoteId, nextDate) => {
        if (!quoteId || !nextDate) return [];

        const movingQuote = (originalAssignment?.job_quotes || []).find(q => q.id === quoteId);
        if (!movingQuote) return [];

        const movingType = movingQuote.quote_type;
        const movingOrder = DEPARTMENT_ORDER[movingType];

        if (movingOrder === undefined) return [];

        const newTime = toDayTime(nextDate);
        if (!newTime) return [];

        const quoteOverrides = {
            ...quoteDates,
            [quoteId]: nextDate,
        };

        return (originalAssignment?.job_quotes || [])
            .filter(q => {
                if (!q) return false;
                if (q.id === quoteId) return false;
                if (q.is_required === false) return false;

                // Ignore re-delivery rows
                if (q.delivery_count && q.delivery_count !== 0) return false;

                const otherOrder = DEPARTMENT_ORDER[q.quote_type];
                if (otherOrder === undefined) return false;

                const otherDate = getQuoteDateForWarning(q, quoteOverrides);
                const otherTime = toDayTime(otherDate);

                if (!otherTime) return false;

                const earlierDepartmentIsAfterNewDate =
                    otherOrder < movingOrder && otherTime > newTime;

                const laterDepartmentIsBeforeNewDate =
                    otherOrder > movingOrder && otherTime < newTime;

                return earlierDepartmentIsAfterNewDate || laterDepartmentIsBeforeNewDate;
            })
            .map(q => {
                const otherOrder = DEPARTMENT_ORDER[q.quote_type];

                return {
                    id: q.id,
                    quote_type: q.quote_type,
                    child_unique_id: q.child_unique_id || q.quote_type,
                    date: getQuoteDateForWarning(q, quoteOverrides),
                    message:
                        otherOrder < movingOrder
                            ? `${q.child_unique_id || q.quote_type} is a ${q.quote_type} job currently set after this new ${movingType} date.`
                            : `${q.child_unique_id || q.quote_type} is a ${q.quote_type} job currently set before this new ${movingType} date.`,
                };
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const actuallyUpdateQuoteDate = async (quoteId, dateStr) => {
        setQuoteDates(prev => ({ ...prev, [quoteId]: dateStr }));
        try {
            await updateSingleQuoteDate(quoteId, dateStr || null);
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;
            logAction({ ...logBase(), action: 'update_quote_date', quote_type: qt, details: dateStr ? `Set finish date for #${label} to ${dateStr}` : `Cleared finish date for #${label}` });

            const displayId = row?.child_unique_id || `${(originalAssignment?.unique_id || originalAssignment?.uniqueId)} (${qt})`;

            let details;
            let actionType;

            if (!dateStr) {
                details = `Removed #${displayId} from schedule`;
                actionType = 'unschedule';
            } else {
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const dateObj = new Date(dateStr);
                const dateToDay = dateObj.getDay();

                details = `Scheduled #${displayId} to ${days[dateToDay]} (${format(dateObj, 'd MMM')})`;
                actionType = 'reschedule';
            }

            createJobActionLog({
                job_id: row?.job_id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: actionType,
                details,
                quote_type: qt || null,
                action_origin: `schedule-${qt}`,
                company_id: company.id
            }).catch(() => { });

            createJobActionLog({
                job_id: row?.job_id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: actionType,
                details,
                quote_type: qt || null,
                action_origin: `schedule-delivery`,
                company_id: company.id
            }).catch(() => { });
        } catch (err) {
            console.error('Failed to update quote date:', err);
        }
    };

    const handleQuoteDateChange = async (quoteId, dateStr) => {
        const warnings = getEtaDateOrderWarnings(quoteId, dateStr);

        if (warnings.length > 0) {
            const row = (originalAssignment?.job_quotes || []).find(q => q.id === quoteId);

            setQuoteDates(prev => ({ ...prev, [quoteId]: dateStr }));

            setQuoteDateWarningPopup({
                quoteId,
                dateStr,
                row,
                warnings,
            });

            setShowQuoteDateWarningDetails(false);
            return;
        }

        await actuallyUpdateQuoteDate(quoteId, dateStr);
    };

    const handleConfirmQuoteDateWarning = async () => {
        if (!quoteDateWarningPopup) return;

        const { quoteId, dateStr } = quoteDateWarningPopup;

        setQuoteDateWarningPopup(null);
        setShowQuoteDateWarningDetails(false);

        await actuallyUpdateQuoteDate(quoteId, dateStr);
    };

    const handleCancelQuoteDateWarning = () => {
        if (quoteDateWarningPopup) {
            const { quoteId } = quoteDateWarningPopup;
            const originalRow = (originalAssignment?.job_quotes || []).find(q => q.id === quoteId);

            const originalDate = originalRow?.expected_delivery_date || originalRow?.expected_finish_date || '';

            setQuoteDates(prev => ({
                ...prev,
                [quoteId]: originalDate ? new Date(originalDate).toISOString().slice(0, 10) : '',
            }));
        }

        setQuoteDateWarningPopup(null);
        setShowQuoteDateWarningDetails(false);
    };

    const handleQuoteInvoiceStateChange = async (quoteId, invoiceState) => {
        const prevState = quoteInvoiceStates[quoteId];
        setQuoteInvoiceStates(prev => ({ ...prev, [quoteId]: invoiceState }));
        try {
            await updateQuoteInvoiceState(quoteId, invoiceState);
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;
            logAction({ ...logBase(), action: 'update_invoice_state', quote_type: qt, details: `Changed invoice state for #${label} from "${prevState || 'not_invoiced'}" to "${invoiceState}"` });
        } catch (err) {
            console.error('Failed to update invoice state:', err);
            setQuoteInvoiceStates(prev => ({ ...prev, [quoteId]: prevState }));
        }
    };

    const handleQuoteTimeSlotChange = async (quoteId, value) => {
        setQuoteTimeSlots(prev => ({ ...prev, [quoteId]: value }));
        try {
            await updateQuoteDeliveryField(quoteId, 'delivery_time_slot', value || null);
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;
            logAction({ ...logBase(), action: 'update_time_slot', quote_type: qt, details: value ? `Set time slot for #${label} to "${value}"` : `Cleared time slot for #${label}` });
        } catch (err) {
            console.error('Failed to update time slot:', err);
        }
    };

    const handleQuoteDeliveryTagChange = async (quoteId, value) => {
        setQuoteDeliveryTags(prev => ({ ...prev, [quoteId]: value }));
        try {
            await updateQuoteDeliveryField(quoteId, 'delivery_tag', value || null);
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;
            logAction({ ...logBase(), action: 'update_delivery_tag', quote_type: qt, details: value ? `Set delivery tag for #${label} to "${value}"` : `Cleared delivery tag for #${label}` });
        } catch (err) {
            console.error('Failed to update delivery tag:', err);
        }
    };

    const handleSaveDeliveryNote = async (quoteId) => {
        const value = editingNoteValue;
        setQuoteDeliveryNotes(prev => ({ ...prev, [quoteId]: value }));
        setEditingNoteId(null);
        try {
            await updateQuoteDeliveryField(quoteId, 'delivery_note', value || null);
            const qt = quoteTypeById(quoteId);
            const row = (originalAssignment?.job_quotes || []).find(r => r.id === quoteId);
            const label = row?.child_unique_id || qt || quoteId;
            logAction({ ...logBase(), action: 'update_delivery_note', quote_type: qt, details: value ? `Updated delivery note for #${label}` : `Cleared delivery note for #${label}` });
        } catch (err) {
            console.error('Failed to update delivery note:', err);
            setQuoteDeliveryNotes(prev => ({ ...prev, [quoteId]: prev[quoteId] }));
        }
    };

    const handlePdfsChange = (pdfFiles) => {
        setNewFiles(pdfFiles);
    };

    const markFileForDeletion = (fileId) => {
        setFilesToDelete(prev => [...prev, fileId]);
    };

    const unmarkFileForDeletion = (fileId) => {
        setFilesToDelete(prev => prev.filter(id => id !== fileId));
    };

    const syncFileOnlyDepartmentStatuses = async () => {
        const fileOnlyTypes = ["beams", "flooring_sheets"];

        for (const quoteType of fileOnlyTypes) {
            const existingFiles = (originalAssignment?.job_files || []).filter(file =>
                file.quote_type === quoteType &&
                file.filename !== "__folder_placeholder__" &&
                file.placeholder_by_default !== true &&
                !filesToDelete.includes(file.id)
            );

            const uploadedFiles = (newFiles || []).filter(fileData =>
                fileData.quoteType === quoteType
            );

            const hasFilesAfterSave = existingFiles.length > 0 || uploadedFiles.length > 0;


            await supabase
                .from("job_quotes")
                .update({
                    department_file_status: hasFilesAfterSave ? "completed" : "waiting_files",
                    department_files_completed_at: hasFilesAfterSave ? new Date().toISOString() : null,
                    department_files_completed_by: hasFilesAfterSave ? userProfile.id : null,
                    department_files_completed_by_name: hasFilesAfterSave
                        ? (userProfile.full_name || userProfile.username)
                        : null,
                })
                .eq("job_id", originalAssignment.id)
                .eq("quote_type", quoteType);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.customerName.trim()) {
            newErrors.customerName = "Customer name is required";
        }

        // Timber jobs skip the Roof/Wall/Floor requirement
        if (formData.jobType !== 'Timber') {
            const hasSelectedQuotes =
                formData.quotes.roof?.isRequired ||
                formData.quotes.wall?.isRequired ||
                formData.quotes.floor?.isRequired ||
                enabledExtraQuoteTypes.some(type => formData.quotes?.[type]?.isRequired);

            if (!hasSelectedQuotes) {
                newErrors.quotes = "At least one section must be selected";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            alert("Please fix the validation errors before submitting.");
            return;
        }

        setIsSubmitting(true);

        try {
            // Departments that are currently checked as required
            const requiredDepts = Object.entries(formData.quotes)
                .filter(([, q]) => q.isRequired)
                .map(([type]) => type);

            // Derive current storey and unit count from form state
            const currentStorey = formData.isSingleStorey !== false
                ? 'single'
                : (formData.isTripleStorey ? 'triple' : 'double');
            const currentUnits = (formData.isUnit && formData.unitCount)
                ? parseInt(formData.unitCount)
                : 1;

            // Update main job data
            const jobUpdateData = {
                unique_id: formData.uniqueId,
                customer_name: formData.customerName,
                customer_number: formData.customerNumber ? `+61${formData.customerNumber}` : null,
                customer_mail: formData.customerMail,
                carpenter_supervisor_name: formData.carpenterSupervisorName,
                carpenter_supervisor_number: formData.carpenterSupervisorNumber ? `+61${formData.carpenterSupervisorNumber}` : null,
                customer_address: formData.customerAddress,
                job_type: formData.jobType,
                roof_type: formData.roofType,
                roof_truss_centres: formData.roofTrussCentres,
                is_single_storey: formData.isSingleStorey,
                storey: currentStorey,
                // site_measure_required: formData.siteMeasureRequired,
                // site_measure_wall: formData.siteMeasureWall,
                // site_measure_floor: formData.siteMeasureFloor,
                // site_measure_truss: formData.siteMeasureTruss,
                is_unit: formData.isUnit,
                unit_count: formData.unitCount ? parseInt(formData.unitCount) : null,
                urgent_quote: formData.urgentQuote,
                urgent_design: formData.urgentDesign,
                // expected_estimation_finish_date: formData.expectedEstimationFinishDate || null,
                // Keep selected_departments in sync for new-system jobs.
                // Old jobs where selected_departments is NULL are left untouched.
                ...(Array.isArray(originalAssignment.selected_departments) && {
                    selected_departments: requiredDepts,
                }),
            };

            await updateAssignment(originalAssignment.id, jobUpdateData, company.id);

            // For new-system jobs: create any job_quotes rows that the DB trigger
            // would have created on insert but are missing (e.g. storey change,
            // unit count change, or newly enabled departments).
            await syncJobQuotesAfterEdit(originalAssignment, requiredDepts, formData.jobType, currentStorey, currentUnits);

            // Update quotes (is_required, dates, invoiced) on existing parent-level rows (old-system).
            if (!originalAssignment?.is_parent) {
                await updateJobQuotes(originalAssignment.id, formData.quotes);
            }

            // New-system jobs: propagate dates to all child quote rows per type.
            // if (originalAssignment?.is_parent) {
            //     await updateChildQuotesDates(originalAssignment.id, formData.quotes);
            // }

            // Timber: job_quotes row has child_unique_id = null (parent-level), so
            // updateChildQuotesDates misses it. Run updateJobQuotes to persist invoiced + dates.
            // Mirror expectedDeliveryDate → expectedFinishDate so clearing the date field
            // nulls both columns (avoids the || fallback keeping the old finish date).
            if (isTimberJob) {
                // const timberDeliveryDate = formData.quotes.timber?.expectedDeliveryDate || "";
                await updateJobQuotes(originalAssignment.id, {
                    timber: {
                        ...formData.quotes.timber,
                        // expectedFinishDate: timberDeliveryDate,
                    }
                });
            }

            // Update floors if floor quote is required
            if (formData.quotes.floor?.isRequired) {
                await updateJobFloors(originalAssignment.id, formData.floors);
            }

            // Update floor specs if wall quote is required
            if (formData.quotes.wall?.isRequired) {
                await updateJobFloorSpecs(originalAssignment.id, {
                    isSingleStorey: formData.isSingleStorey,
                    isTripleStorey: formData.isTripleStorey,
                    singleStorey: formData.singleStorey,
                    doubleStorey: formData.doubleStorey,
                    tripleStorey: formData.tripleStorey
                });
            }

            // Delete marked files
            if (filesToDelete.length > 0) {
                await deleteJobFiles(filesToDelete, originalAssignment.id, originalAssignment.unique_id, userProfile);
            }

            // Upload new files
            if (newFiles?.length > 0) {
                for (const fileData of newFiles) {
                    await uploadJobFiles(
                        originalAssignment.id,
                        [fileData.file],  // Pass the actual file
                        userProfile.id,
                        userProfile.full_name || userProfile.username,
                        companySlug || company?.slug,
                        fileData.category,  // Pass category
                        fileData.quoteType   // Pass quoteType,
                    );
                }
            }

            await syncFileOnlyDepartmentStatuses();

            // ── Diff old vs new and build a meaningful log entry ────────────
            {
                const FIELD_LABELS = {
                    unique_id: 'Job ID', customer_name: 'Customer', customer_number: 'Phone',
                    customer_mail: 'Email', carpenter_supervisor_name: 'Supervisor',
                    carpenter_supervisor_number: 'Supervisor phone', customer_address: 'Address',
                    job_type: 'Job type', roof_type: 'Roof type', roof_truss_centres: 'Truss centres',
                    is_single_storey: 'Single storey', storey: 'Storey', site_measure_required: 'Site measure',
                    site_measure_wall: 'SM wall', site_measure_floor: 'SM floor', site_measure_truss: 'SM truss',
                    is_unit: 'Is unit', unit_count: 'Unit count', urgent_quote: 'Urgent quote',
                    urgent_design: 'Urgent design', selected_departments: 'Departments',
                };
                const snap = originalAssignment;
                const oldVals = {};
                const newVals = {};
                const changedFields = [];
                for (const [key, newVal] of Object.entries(jobUpdateData)) {
                    const oldVal = snap[key];
                    const oldStr = JSON.stringify(oldVal ?? null);
                    const newStr = JSON.stringify(newVal ?? null);
                    if (oldStr !== newStr) {
                        changedFields.push(key);
                        oldVals[key] = oldVal ?? null;
                        newVals[key] = newVal ?? null;
                    }
                }
                const count = changedFields.length;
                const labelList = changedFields.map(f => FIELD_LABELS[f] || f).join(', ');
                const details = count === 0
                    ? 'Job saved (no field changes detected)'
                    : `Updated ${count} field${count !== 1 ? 's' : ''}: ${labelList}`;
                createJobActionLog({
                    job_id: originalAssignment.id,
                    user_id: userProfile.id,
                    user_name: userProfile.full_name || userProfile.username || userProfile.email || 'Unknown',
                    user_role: userProfile.role,
                    action: 'job_updated',
                    details,
                    changed_fields: changedFields.length ? changedFields : null,
                    old_values: changedFields.length ? oldVals : null,
                    new_values: changedFields.length ? newVals : null,
                    action_origin: 'edit-assignment',
                }).catch(() => { });
            }

            alert("Assignment updated successfully!");
            if (isModal) { onClose(true); } else { navigate(`/${companySlug}/assignment/${jobId}`); }

        } catch (error) {
            console.error("Error updating assignment:", error);
            alert(error.message || "Error updating assignment");
        } finally {
            setIsSubmitting(false);
        }
    };

    // formKey → DB column on jobs table
    const SM_FORM_TO_DB = {
        siteMeasureWall: 'site_measure_wall',
        siteMeasureFloor: 'site_measure_floor',
        siteMeasureTruss: 'site_measure_truss',
    };

    const SM_LABELS = {
        siteMeasureWall: 'Site Measure Wall',
        siteMeasureFloor: 'Site Measure Floor',
        siteMeasureTruss: 'Site Measure Truss',
    };

    const handleSiteMeasureToggle = async (formKey) => {
        const dbKey = SM_FORM_TO_DB[formKey];
        const oldValue = !!formData[formKey];
        const newValue = !oldValue;
        const label = SM_LABELS[formKey] || formKey;

        // instant UI update
        setFormData(prev => ({
            ...prev,
            [formKey]: newValue,
        }));

        try {
            await updateAssignment(
                originalAssignment.id,
                { [dbKey]: newValue },
                company.id
            );

            createJobActionLog({
                job_id: originalAssignment.id,
                company_id: company.id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: 'site_measure_toggle',
                details: `${label} turned ${newValue ? 'on' : 'off'}`,
                changed_fields: [dbKey],
                old_values: { [dbKey]: oldValue },
                new_values: { [dbKey]: newValue },
                action_origin: 'edit-assignment',
            }).catch(() => { });

        } catch (err) {
            console.error('Failed to update site measure:', err);

            // rollback UI
            setFormData(prev => ({
                ...prev,
                [formKey]: oldValue,
            }));

            alert('Failed to update site measure. Please try again.');
        }
    };

    const handleAdditionalDepartmentToggle = async (quoteType) => {
        const oldValue = !!formData.quotes?.[quoteType]?.isRequired;
        const newValue = !oldValue;

        const oldSelectedDepartments = Array.isArray(originalAssignment?.selected_departments)
            ? originalAssignment.selected_departments
            : (originalAssignment?.job_quotes || [])
                .filter(q =>
                    q.is_required &&
                    (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined)
                )
                .map(q => q.quote_type);

        const nextSelectedDepartments = newValue
            ? [...new Set([...oldSelectedDepartments, quoteType])]
            : oldSelectedDepartments.filter(t => t !== quoteType);

        setFormData(prev => ({
            ...prev,
            quotes: {
                ...prev.quotes,
                [quoteType]: {
                    ...(prev.quotes?.[quoteType] || {}),
                    isRequired: newValue,
                },
            },
        }));

        setChildActionLoading(quoteType);

        try {
            const existingQuote = (originalAssignment?.job_quotes || []).find(q =>
                q.quote_type === quoteType &&
                (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined)
            );

            if (existingQuote?.id) {
                await setChildQuoteRequired(existingQuote.id, newValue);

                setChildDepts(prev =>
                    prev.map(q =>
                        q.id === existingQuote.id
                            ? { ...q, is_required: newValue }
                            : q
                    )
                );

                setOriginalAssignment(prev => ({
                    ...prev,
                    selected_departments: nextSelectedDepartments,
                    job_quotes: (prev.job_quotes || []).map(q =>
                        q.id === existingQuote.id
                            ? { ...q, is_required: newValue }
                            : q
                    ),
                }));
            } else if (newValue) {
                const { data: createdQuote, error } = await supabase
                    .from("job_quotes")
                    .insert({
                        job_id: originalAssignment.id,
                        quote_type: quoteType,
                        is_required: true,

                        design_status: "not_required",
                        estimation_status: "not_required",
                        production_status: "not_started",
                        delivery_status: "not_scheduled",

                        department_file_status: "waiting_files",

                        expected_finish_date: formData.quotes?.[quoteType]?.expectedFinishDate || null,
                        expected_delivery_date: formData.quotes?.[quoteType]?.expectedDeliveryDate || null,

                        child_unique_id: null,
                        delivery_count: null,
                    })
                    .select("*")
                    .single();

                if (error) throw error;

                setChildDepts(prev => [...prev, createdQuote]);

                setOriginalAssignment(prev => ({
                    ...prev,
                    job_quotes: [...(prev.job_quotes || []), createdQuote],
                }));
            }

            await updateAssignment(
                originalAssignment.id,
                { selected_departments: nextSelectedDepartments },
                company.id
            );

            setOriginalAssignment(prev => ({
                ...prev,
                selected_departments: nextSelectedDepartments,
            }));

            createJobActionLog({
                job_id: originalAssignment.id,
                company_id: company.id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || "Unknown",
                user_role: userProfile?.role,
                action: "additional_department_toggle",
                details: `${getQuoteLabel(quoteType)} turned ${newValue ? "on" : "off"}`,
                quote_type: quoteType,
                changed_fields: ["selected_departments"],
                old_values: { selected_departments: oldSelectedDepartments },
                new_values: { selected_departments: nextSelectedDepartments },
                action_origin: "edit-assignment",
            }).catch(() => { });
        } catch (err) {
            console.error("Failed to update additional department:", err);

            setFormData(prev => ({
                ...prev,
                quotes: {
                    ...prev.quotes,
                    [quoteType]: {
                        ...(prev.quotes?.[quoteType] || {}),
                        isRequired: oldValue,
                    },
                },
            }));

            alert("Failed to update additional department. Please try again.");
        } finally {
            setChildActionLoading(null);
        }
    };

    const formatSmDate = (dateStr) => {
        if (!dateStr) return 'empty';

        const dateOnly = String(dateStr).slice(0, 10);
        const [year, month, day] = dateOnly.split('-');

        if (!year || !month || !day) return String(dateStr);

        return `${day}/${month}/${year}`;
    };

    // ── Site measure date popup submit (instant save — no main Save needed) ──
    const handleSmPopupSubmit = async () => {
        if (!smPopup?.date || smSaving) return;
        setSmSaving(true);
        try {
            const oldQuoteRow = originalAssignment?.job_quotes?.find(
                q => q.id === smPopup.quoteId
            );
            const oldDate = oldQuoteRow?.site_measure_date || null;

            const [y, mo, d] = smPopup.date.split('-').map(Number);
            const dateObj = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
            const dbBoolKey = SM_FORM_TO_DB[smPopup.formKey];
            const [dateResult] = await Promise.all([
                updateSiteMeasureDate(smPopup.quoteId, dateObj),
                updateAssignment(originalAssignment.id, { [dbBoolKey]: true }, company.id),
            ]);


            createJobActionLog({
                job_id: originalAssignment.id,
                company_id: company.id,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: 'site_measure_date_change',
                details: `${smPopup?.label} (${smPopup?.quoteType}) date changed from ${formatSmDate(oldDate)} to ${formatSmDate(smPopup?.date)}`,
                quote_type: smPopup?.quoteType || null,
                changed_fields: ['site_measure_date'],
                old_values: { site_measure_date: formatSmDate(oldDate) },
                new_values: { site_measure_date: formatSmDate(smPopup?.date) },
                action_origin: 'edit-assignment',
            }).catch(() => { });

            console.log(smPopup)

            if (!dateResult.error) {
                setChildDepts(prev => prev.map(q =>
                    q.id === smPopup.quoteId ? { ...q, site_measure_date: smPopup.date } : q
                ));
                setFormData(prev => ({ ...prev, [smPopup.formKey]: true }));
                setSmPopup(null);
            }
        } finally {
            setSmSaving(false);
        }
    };

    // ── Clear date + uncheck (instant save) ───────────────────────────────────
    const handleSmClearDate = async () => {
        if (smSaving) return;
        setSmSaving(true);
        try {
            const dbBoolKey = SM_FORM_TO_DB[smPopup.formKey];

            const oldQuoteRow = originalAssignment?.job_quotes?.find(
                q => q.id === smPopup.quoteId
            );
            const oldDate = oldQuoteRow?.site_measure_date || null;

            await Promise.all([
                updateSiteMeasureDate(smPopup.quoteId, null),
                // updateAssignment(originalAssignment.id, { [dbBoolKey]: false }, company.id),
            ]);

            createJobActionLog({
                job_id: originalAssignment.id,
                company_id: company.id,
                user_id: userProfile?.id,
                user_name:
                    userProfile?.full_name ||
                    userProfile?.username ||
                    userProfile?.email ||
                    'Unknown',
                user_role: userProfile?.role,
                action: 'site_measure_date_remove',
                details: `${smPopup?.label} (${smPopup?.quoteType}) date changed from ${formatSmDate(oldDate)} to empty`,
                quote_type: smPopup?.quoteType || null,
                changed_fields: ['site_measure_date'],
                old_values: {
                    site_measure_date: formatSmDate(oldDate),
                },
                new_values: {
                    site_measure_date: null,
                },
                action_origin: 'edit-assignment',
            }).catch(() => { });

            setChildDepts(prev => prev.map(q =>
                q.id === smPopup.quoteId ? { ...q, site_measure_date: null } : q
            ));
            // setFormData(prev => ({ ...prev, [smPopup.formKey]: false }));
            setSmPopup(null);
        } finally {
            setSmSaving(false);
        }
    };

    const capitalizeFirstLetter = (val) => {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    const FILE_ICON_MAP = {
        image: {
            extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'],
            icon: <FiImage size={22} color="GRAY" />
        },
        pdf: {
            extensions: ['pdf'],
            icon: <FaRegFilePdf size={22} color="GRAY" />
        },
        word: {
            extensions: ['doc', 'docx'],
            icon: <FaRegFileWord size={22} color="GRAY" />
        },
        excel: {
            extensions: ['xls', 'xlsx', 'csv'],
            icon: <FileSpreadsheet size={22} color="GRAY" />
        },
        audio: {
            extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
            icon: <FileAudio size={22} color="GRAY" />
        },
        video: {
            extensions: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
            icon: <FileVideo size={22} color="GRAY" />
        },
        archive: {
            extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
            icon: <FileArchive size={22} color="GRAY" />
        },
        code: {
            extensions: ['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'py', 'java', 'php'],
            icon: <Code size={22} color="GRAY" />
        },
        text: {
            extensions: ['txt', 'md', 'log'],
            icon: <FiFileText size={22} color="GRAY" />
        }
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        for (const type in FILE_ICON_MAP) {
            if (FILE_ICON_MAP[type].extensions.includes(extension)) {
                return FILE_ICON_MAP[type].icon;
            }
        }

        return <FiFile size={22} color="GRAY" />; // default icon
    };

    const visibleExistingFiles = useMemo(() => {
        const query = fileSearch.trim().toLowerCase();

        return (originalAssignment?.job_files || [])
            .filter(file => !filesToDelete.includes(file.id))
            .filter(file => file.filename !== "__folder_placeholder__")
            .filter(file => {
                if (!query) return true;

                return [
                    file.original_name,
                    file.filename,
                    file.file_type,
                    file.quote_type,
                    file.description,
                    file.uploaded_by_name,
                    file.uploaded_by_role,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(query);
            });
    }, [originalAssignment?.job_files, filesToDelete, fileSearch]);

    const hasFileSearch = fileSearch.trim().length > 0;

    if (loading || !originalAssignment) {
        const spinner = (
            <div className="flex items-center justify-center min-h-[300px] !mt-0 px-4 py-4 rounded-lg">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading assignment details...</p>
                </div>
            </div>
        );
        if (isModal) {
            return (
                <div className="mobile-dialog-shell fixed inset-0 z-[200] flex justify-end !mt-0 px-4 py-4 rounded-lg">
                    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
                    <div className="relative w-full max-w-3xl h-full overflow-y-auto bg-white dark:bg-zinc-900 shadow-2xl p-6 sm:p-8">
                        {spinner}
                    </div>
                </div>
            );
        }
        return (
            <div className="flex items-center justify-center min-h-screen !mt-0 px-4 py-4 rounded-lg">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    const isTimberJob = formData.jobType === 'Timber' || originalAssignment?.job_type === 'Timber';

    const formContent = (
        <form onSubmit={handleSubmit} className="mobile-detail-page edit-job-page space-y-8">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-gray-200 dark:border-zinc-700 pb-6">
                <div className="flex items-start gap-4">
                    <button
                        type="button"
                        onClick={() => isModal ? onClose() : navigate(`/${companySlug}/assignment/${jobId}`, {
                            state: { from: location.pathname }
                        })}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                    >
                        {isModal ? <FiX className="w-5 h-5" /> : <FiArrowLeft className="w-5 h-5" />}
                    </button>
                    <div>
                        <h1 className="sm:text-3xl text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Edit {originalAssignment.unique_id}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800">
                                EDITING
                            </span>
                            <span className="px-3 py-1 bg-gray-100 dark:bg-zinc-700 text-gray-800 dark:text-gray-300 rounded-full text-xs font-medium">
                                {originalAssignment.job_type}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Job ID Section */}
            <InfoCard title="Job Identification" icon={FiFileText}>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Job ID *
                    </label>
                    <input
                        type="text"
                        name="uniqueId"
                        value={formData.uniqueId}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Enter unique job ID"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        This is the unique identifier for this job assignment
                    </p>
                </div>
            </InfoCard>

            {/* Customer Information */}
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                <InfoCard title="Customer Information" icon={FiUser}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Customer Name *
                        </label>
                        <input
                            type="text"
                            name="customerName"
                            value={formData.customerName}
                            onChange={handleInputChange}
                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.customerName ? 'border-red-500' : 'border-gray-300 dark:border-zinc-600'
                                }`}
                            placeholder="Enter customer name"
                        />
                        {errors.customerName && (
                            <p className="text-red-500 text-xs mt-1">{errors.customerName}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Customer Phone Number
                        </label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 text-sm rounded-l-lg">
                                +61
                            </span>
                            <input
                                type="tel"
                                name="customerNumber"
                                value={formatPhoneDisplay(formData.customerNumber)}
                                onChange={handleInputChange}
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-r-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="XXX XXX XXX"
                                maxLength={20}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Customer Email
                        </label>
                        <input
                            type="email"
                            name="customerMail"
                            value={formData.customerMail}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter email address"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Job Address
                        </label>
                        <textarea
                            name="customerAddress"
                            value={formData.customerAddress}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter job address"
                        />
                    </div>
                </InfoCard>

                {/* Supervisor Information */}
                {!isTimberJob && <InfoCard title="Supervisor Information" icon={FiTool}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Supervisor Name
                        </label>
                        <input
                            type="text"
                            name="carpenterSupervisorName"
                            value={formData.carpenterSupervisorName}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Enter supervisor name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Supervisor Phone Number
                        </label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 text-sm rounded-l-lg">
                                +61
                            </span>
                            <input
                                type="tel"
                                name="carpenterSupervisorNumber"
                                value={formatPhoneDisplay(formData.carpenterSupervisorNumber)}
                                onChange={handleInputChange}
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-r-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="XXX XXX XXX"
                                maxLength={20}
                            />
                        </div>
                    </div>
                </InfoCard>}
            </div>

            {/* Timber Delivery Date */}
            {/* {isTimberJob && (
                <div className="space-y-4">
                    <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-amber-400 pb-2">
                        Delivery Information
                    </h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Expected Delivery Date
                        </label>
                        <input
                            className="w-full pl-9 px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            type="date"
                            value={formData?.quotes?.timber?.expectedDeliveryDate || ""}
                            onChange={(e) => handleNestedChange("quotes.timber.expectedDeliveryDate", e.target.value)}
                        />
                    </div>
                </div>
            )} */}

            {/* Job Requirements */}
            {!isTimberJob && <InfoCard title="Job Requirements" icon={FiLayers}>
                <div className="!mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Job Type *
                    </label>
                    <div className="sm:flex-row flex gap-3 flex-col">
                        <SelectionButton
                            label="Design Only"
                            isSelected={formData.jobType === "Design"}
                            onClick={() => setFormData(prev => ({ ...prev, jobType: "Design" }))}
                        />
                        <SelectionButton
                            label="Estimation Only"
                            isSelected={formData.jobType === "Estimation"}
                            onClick={() => setFormData(prev => ({ ...prev, jobType: "Estimation" }))}
                        />
                        <SelectionButton
                            label="Both Design & Estimation"
                            isSelected={formData.jobType === "Design & Estimation"}
                            onClick={() => setFormData(prev => ({ ...prev, jobType: "Design & Estimation" }))}
                        />
                    </div>
                </div>

                {!isTimberJob && hasExtraQuoteTypes && (
                    <div className="pt-4 pb-4 mb-4 border-b border-gray-200 dark:border-zinc-700 space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Additional Departments
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                These departments are file-only. Toggling only changes whether the department is required.
                            </p>
                        </div>

                        <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                            {enabledExtraQuoteTypes.map(type => {
                                const Icon = getQuoteTypeIconComponent(type);
                                const checked = !!formData.quotes?.[type]?.isRequired;
                                const loading = childActionLoading === type;

                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        disabled={loading}
                                        onClick={() => handleAdditionalDepartmentToggle(type)}
                                        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${checked
                                            ? "bg-green-500 text-white border-green-500 shadow-md"
                                            : "bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                                            }`}
                                    >
                                        {checked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
                                        <Icon className="w-4 h-4" />
                                        {getQuoteLabel(type)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="!mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Required Sections *
                    </label>
                    <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                        <CheckboxButton
                            label="Roof"
                            isChecked={formData.quotes.roof?.isRequired}
                            onClick={() => handleNestedChange('quotes.roof.isRequired', !formData.quotes.roof?.isRequired)}
                        />
                        <CheckboxButton
                            label="Wall"
                            isChecked={formData.quotes.wall?.isRequired}
                            onClick={() => handleNestedChange('quotes.wall.isRequired', !formData.quotes.wall?.isRequired)}
                        />
                        <div>
                            <CheckboxButton
                                label="Floor"
                                isChecked={formData.quotes.floor?.isRequired}
                                onClick={() => {
                                    // if (!formData.quotes.floor?.isRequired && formData.isSingleStorey === true) return;
                                    handleNestedChange('quotes.floor.isRequired', !formData.quotes.floor?.isRequired);
                                }}
                            />
                            {/* {formData.isSingleStorey === true && (
                                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                    ⚠ Single Storey and Floor cannot be selected together
                                </p>
                            )} */}
                        </div>
                    </div>
                    {errors.quotes && (
                        <p className="text-red-500 text-xs mt-1">{errors.quotes}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Priority Settings
                    </label>
                    <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                        <CheckboxButton
                            label="Urgent Quote"
                            isChecked={formData.urgentQuote}
                            onClick={() => setFormData(prev => ({ ...prev, urgentQuote: !prev.urgentQuote }))}
                        />
                        <CheckboxButton
                            label="Urgent Design"


                            isChecked={formData.urgentDesign}
                            onClick={() => setFormData(prev => ({ ...prev, urgentDesign: !prev.urgentDesign }))}
                        />
                    </div>
                </div>
            </InfoCard>}

            {/* ── Dates section ── hidden for Estimation jobs */}
            {formData.jobType !== 'Estimation' && (() => {
                const isNewSystem = (originalAssignment?.selected_departments?.length ?? 0) > 0;
                // Show all required job_quotes rows (both parent and child), excluding redeliveries
                const allRequiredRows = (originalAssignment?.job_quotes || []).filter(q =>
                    q.is_required !== false &&
                    (q.delivery_count === null || q.delivery_count === 0 || q.delivery_count === undefined)
                );
                // For new-system jobs, parent rows have no child_unique_id and is_required may be false/null —
                // use selected_departments to determine which parent-type rows to include
                const selectedDeptSet = new Set(originalAssignment?.selected_departments || []);
                const dateRows = allRequiredRows
                    .filter(q => {
                        if (q.child_unique_id) return true; // always include child rows
                        if (isNewSystem) return selectedDeptSet.has(q.quote_type); // new system: only selected types
                        return q.is_required; // old system: is_required flag
                    })
                    .sort((a, b) => {
                        const t = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                        if (t !== 0) return t;
                        return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                    });

                if (dateRows.length === 0) return null;

                const groupedDateRows = getSortedQuoteTypeEntries(dateRows);

                return (
                    <DropdownSection
                        title="ETA Dates"
                        titleStyle="text-zinc-700 text-sm"
                        icon={FiCalendar}
                        iconStyle="text-zinc-800"
                        subtext=""
                        subtextStyle=""
                        defaultOpen={false}
                    >
                        <div className="space-y-3">
                            {groupedDateRows.map(group => (
                                <DropdownSection
                                    key={group.quoteType}
                                    title={group.label}
                                    titleStyle="text-zinc-700 text-sm"
                                    icon={getQuoteTypeIconComponent(group.quoteType)}
                                    iconStyle="text-zinc-700 self-center items-center !w-4 !h-4"
                                    subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                    subtextStyle="text-zinc-400 italic text-xs"
                                    defaultOpen={false}
                                >
                                    <div className="space-y-2">
                                        {group.items.map(row => {
                                            const defaultDate = row.expected_delivery_date
                                                ? new Date(row.expected_delivery_date).toISOString().slice(0, 10)
                                                : '';
                                            const currentDate = quoteDates[row.id] !== undefined ? quoteDates[row.id] : defaultDate;

                                            return (
                                                <div
                                                    key={row.id}
                                                    className="items-center gap-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2 flex flex-col"
                                                >
                                                    <div className="flex flex-row items-center w-full">
                                                        <div className="flex items-center gap-1.5 min-w-[80px]">
                                                            {getChildQuoteIcon(row.quote_type)}
                                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                                                {row.quote_type}
                                                            </span>
                                                        </div>

                                                        {row.child_unique_id && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">
                                                                {row.child_unique_id}
                                                            </span>
                                                        )}

                                                        <div className="flex items-center gap-1.5 ml-auto">
                                                            <FiCalendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                            {currentDate && (
                                                                <span className="text-xs mr-2 text-gray-500 dark:text-gray-400 font-mono">
                                                                    {currentDate.split('-').reverse().join('/')}
                                                                </span>
                                                            )}
                                                            <input
                                                                type="date"
                                                                value={currentDate}
                                                                onChange={e => handleQuoteDateChange(row.id, e.target.value)}
                                                                className="text-xs border text-end border-gray-300 dark:border-zinc-600 rounded-lg px-2 py-1 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                    </div>

                                                    {(row?.delivery_playground || row?.quote_playground) && (
                                                        <div className="flex flex-row text-xs gap-2 items-center justify-start self-start">
                                                            <Info className="w-4 h-4 text-red-900" />
                                                            <p className="text-red-700">
                                                                Setting a date will remove this job from <b>Department</b> & <b>Delivery Playground</b>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </DropdownSection>
                            ))}
                        </div>
                    </DropdownSection>
                );
            })()}

            {/* Delivery Section */}
            {(() => {
                const isNewSystem = (originalAssignment?.selected_departments?.length ?? 0) > 0;
                const allRequiredRows = (originalAssignment?.job_quotes || []).filter(q =>
                    q.is_required !== false
                );

                const deliveryRows = allRequiredRows
                    .filter(q => {
                        if (q.delivery_count !== null && q.delivery_count !== 0 && q.delivery_count !== undefined) return false;
                        if (q.child_unique_id) return true;
                        if (isNewSystem) return (originalAssignment?.selected_departments || []).includes(q.quote_type);
                        return q.is_required;
                    })
                    .sort((a, b) => {
                        const t = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                        if (t !== 0) return t;
                        return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                    });

                const groupedDeliveryRows = getSortedQuoteTypeEntries(deliveryRows);

                const TIME_SLOT_OPTIONS = [
                    { value: '', label: '— None —' },
                    { value: 'First Delivery', label: 'First Delivery' },
                    { value: 'Second Delivery', label: 'Second Delivery' },
                    { value: 'Before Lunch', label: 'Before Lunch' },
                    { value: 'Lunchtime', label: 'Lunchtime' },
                    { value: 'After Lunch', label: 'After Lunch' },
                    { value: 'Last Delivery', label: 'Last Delivery' },
                ];

                const DELIVERY_TAG_OPTIONS = [
                    { value: '', label: '— None —' },
                    { value: 'confirmed', label: 'Confirmed' },
                    { value: 'doublecheck', label: 'Double Check' },
                ];

                const QuoteRowShell = ({ row, children }) => (
                    <div className="flex items-center gap-3 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1.5 min-w-[80px]">
                            {getChildQuoteIcon(row.quote_type)}
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                {row.quote_type}
                            </span>
                        </div>

                        {isNewSystem && row.child_unique_id && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">
                                {row.child_unique_id}
                            </span>
                        )}

                        <div className="ml-auto">{children}</div>
                    </div>
                );

                if (deliveryRows.length === 0) return null;

                return (
                    <DropdownSection
                        title="Delivery Settings"
                        titleStyle="text-zinc-700 text-sm"
                        icon={Truck}
                        iconStyle="text-zinc-800"
                        subtext=""
                        subtextStyle=""
                        defaultOpen={false}
                    >
                        <div className="space-y-4">
                            {/* Time Slot */}
                            <DropdownSection
                                title="Time Slot"
                                titleStyle="text-zinc-600 text-[14px]"
                                iconStyle="text-zinc-600"
                                subtext=""
                                subtextStyle=""
                                defaultOpen={false}
                            >
                                <div className="space-y-3">
                                    {groupedDeliveryRows.map(group => (
                                        <DropdownSection
                                            key={`timeslot-${group.quoteType}`}
                                            title={group.label}
                                            titleStyle="text-zinc-700 text-sm"
                                            icon={getQuoteTypeIconComponent(group.quoteType)}
                                            iconStyle="text-zinc-700 self-center items-center !w-4 !h-4"
                                            subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                            subtextStyle="text-zinc-400 italic text-xs"
                                            defaultOpen={false}
                                        >
                                            <div className="space-y-2">
                                                {group.items.map(row => (
                                                    <QuoteRowShell key={row.id} row={row}>
                                                        <select
                                                            value={quoteTimeSlots[row.id] ?? ''}
                                                            onChange={e => handleQuoteTimeSlotChange(row.id, e.target.value)}
                                                            className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2 py-1 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        >
                                                            {TIME_SLOT_OPTIONS.map(o => (
                                                                <option key={o.value} value={o.value}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                    </QuoteRowShell>
                                                ))}
                                            </div>
                                        </DropdownSection>
                                    ))}
                                </div>
                            </DropdownSection>

                            {/* Delivery Tag */}
                            <DropdownSection
                                title="Delivery Tag"
                                titleStyle="text-zinc-600 text-[14px]"
                                iconStyle="text-zinc-600"
                                subtext=""
                                subtextStyle=""
                                defaultOpen={false}
                            >
                                <div className="space-y-3">
                                    {groupedDeliveryRows.map(group => (
                                        <DropdownSection
                                            key={`deliverytag-${group.quoteType}`}
                                            title={group.label}
                                            titleStyle="text-zinc-700 text-sm"
                                            icon={getQuoteTypeIconComponent(group.quoteType)}
                                            iconStyle="text-zinc-700 self-center items-center !w-4 !h-4"
                                            subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                            subtextStyle="text-zinc-400 italic text-xs"
                                            defaultOpen={false}
                                        >
                                            <div className="space-y-2">
                                                {group.items.map(row => (
                                                    <QuoteRowShell key={row.id} row={row}>
                                                        <select
                                                            value={quoteDeliveryTags[row.id] ?? ''}
                                                            onChange={e => handleQuoteDeliveryTagChange(row.id, e.target.value)}
                                                            className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2 py-1 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        >
                                                            {DELIVERY_TAG_OPTIONS.map(o => (
                                                                <option key={o.value} value={o.value}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                    </QuoteRowShell>
                                                ))}
                                            </div>
                                        </DropdownSection>
                                    ))}
                                </div>
                            </DropdownSection>

                            {/* Delivery Note */}
                            <DropdownSection
                                title="Delivery Note"
                                titleStyle="text-zinc-600 text-[14px]"
                                iconStyle="text-zinc-600"
                                subtext=""
                                subtextStyle=""
                                defaultOpen={false}
                            >
                                <div className="space-y-3">
                                    {groupedDeliveryRows.map(group => (
                                        <DropdownSection
                                            key={`deliverynote-${group.quoteType}`}
                                            title={group.label}
                                            titleStyle="text-zinc-700 text-sm"
                                            icon={getQuoteTypeIconComponent(group.quoteType)}
                                            iconStyle="text-zinc-700 self-center items-center !w-4 !h-4"
                                            subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                            subtextStyle="text-zinc-400 italic text-xs"
                                            defaultOpen={false}
                                        >
                                            <div className="space-y-2">
                                                {group.items.map(row => (
                                                    <div
                                                        key={row.id}
                                                        className="flex items-center gap-3 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2"
                                                    >
                                                        <div className="flex items-center gap-1.5 min-w-[80px]">
                                                            {getChildQuoteIcon(row.quote_type)}
                                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                                                {row.quote_type}
                                                            </span>
                                                        </div>

                                                        {isNewSystem && row.child_unique_id && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">
                                                                {row.child_unique_id}
                                                            </span>
                                                        )}

                                                        <div className="flex items-center gap-2 ml-auto min-w-0 flex-1 justify-end">
                                                            {editingNoteId === row.id ? (
                                                                <>
                                                                    <input
                                                                        autoFocus
                                                                        type="text"
                                                                        value={editingNoteValue}
                                                                        onChange={e => setEditingNoteValue(e.target.value)}
                                                                        onKeyDown={e => {
                                                                            if (e.key === 'Enter') handleSaveDeliveryNote(row.id);
                                                                            if (e.key === 'Escape') setEditingNoteId(null);
                                                                        }}
                                                                        className="text-xs border border-blue-400 rounded-lg px-2 py-1 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1 min-w-0 max-w-[180px]"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleSaveDeliveryNote(row.id)}
                                                                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-shrink-0"
                                                                    >
                                                                        Save
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setEditingNoteId(null)}
                                                                        className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
                                                                    >
                                                                        <FiX className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
                                                                        {quoteDeliveryNotes[row.id] || <span className="italic text-gray-400">No note</span>}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setEditingNoteId(row.id);
                                                                            setEditingNoteValue(quoteDeliveryNotes[row.id] || '');
                                                                        }}
                                                                        className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                                                                    >
                                                                        <FiEdit2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </DropdownSection>
                                    ))}
                                </div>
                            </DropdownSection>
                        </div>
                    </DropdownSection>
                );
            })()}


            {/* ── Invoice States section ── */}
            {(() => {
                const isNewSystem = (originalAssignment?.selected_departments?.length ?? 0) > 0;
                const selectedDeptSet = new Set(originalAssignment?.selected_departments || []);

                // All required rows including redeliveries (delivery_count rows)
                const allRequiredRows = (originalAssignment?.job_quotes || []).filter(q =>
                    q.is_required !== false
                );

                const invoiceRows = allRequiredRows
                    .filter(q => {
                        if (q.child_unique_id) return true;
                        if (isNewSystem) return selectedDeptSet.has(q.quote_type);
                        return q.is_required;
                    })
                    .sort((a, b) => {
                        const t = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                        if (t !== 0) return t;

                        const cmp = (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                        if (cmp !== 0) return cmp;

                        return (a.delivery_count || 0) - (b.delivery_count || 0);
                    });

                const groupedInvoiceRows = getSortedQuoteTypeEntries(invoiceRows);

                if (invoiceRows.length === 0) return null;

                return (
                    <DropdownSection
                        title="Invoices"
                        titleStyle="text-zinc-700 text-sm"
                        icon={Receipt}
                        iconStyle="text-zinc-800"
                        subtext=""
                        subtextStyle=""
                        defaultOpen={false}
                    >
                        <div className="space-y-3">
                            {groupedInvoiceRows.map(group => (
                                <DropdownSection
                                    key={`invoice-${group.quoteType}`}
                                    title={group.label}
                                    titleStyle="text-zinc-700 text-sm"
                                    icon={getQuoteTypeIconComponent(group.quoteType)}
                                    iconStyle="text-zinc-700 self-center items-center !w-4 !h-4"
                                    subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                    subtextStyle="text-zinc-400 italic text-xs"
                                    defaultOpen={false}
                                >
                                    <div className="space-y-2">
                                        {group.items.map(row => {
                                            const currentState = quoteInvoiceStates[row.id] !== undefined
                                                ? quoteInvoiceStates[row.id]
                                                : (row.invoice_state || 'not_invoiced');

                                            return (
                                                <div
                                                    key={row.id}
                                                    className="flex items-center gap-3 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2"
                                                >
                                                    <div className="flex items-center gap-1.5 min-w-[80px]">
                                                        {getChildQuoteIcon(row.quote_type)}
                                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                                            {row.quote_type}
                                                        </span>
                                                    </div>

                                                    {row.child_unique_id && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 rounded">
                                                            {row.child_unique_id}
                                                        </span>
                                                    )}

                                                    {row.delivery_count != null && row.delivery_count > 0 && (
                                                        <span className="text-xs font-medium text-blue-700 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                                            Delivery #{row.delivery_count}
                                                        </span>
                                                    )}

                                                    <div className="ml-auto flex items-center gap-2">
                                                        <select
                                                            value={currentState}
                                                            onChange={e => handleQuoteInvoiceStateChange(row.id, e.target.value)}
                                                            className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2 py-1 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        >
                                                            <option value="not_invoiced">Not Invoiced</option>
                                                            <option value="invoiced">Invoiced</option>
                                                            <option value="cod">COD</option>
                                                            <option value="paid">Paid</option>
                                                            <option value="deposit">Deposit</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </DropdownSection>
                            ))}
                        </div>
                    </DropdownSection>
                );
            })()}

            {/* ── Child Departments ── only shown for jobs that have the multi-child system */}
            {/* {(originalAssignment?.selected_departments?.length ?? 0) > 0 && ( */}
            <div className="space-y-3">
                {/* Section header */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-zinc-700 pb-2">
                    <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Child Departments ({childDepts.length})
                    </h2>
                    <button
                        type="button"
                        onClick={openNewChildRow}
                        className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                    >
                        <FiPlus className="w-3.5 h-3.5" />
                        Add Department
                    </button>
                </div>

                {/* New department form row */}
                {newChildRow !== null && (
                    <div className="flex items-end gap-3 flex-wrap p-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-lg">
                        {/* Quote type */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Type
                            </label>
                            <select
                                value={newChildRow.quoteType}
                                onChange={e => setNewChildRow(prev => ({ ...prev, quoteType: e.target.value }))}
                                className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                                {(() => {
                                    // New-system: use selected_departments; old-system: derive from existing required quotes
                                    const depts = (originalAssignment?.selected_departments || []).length > 0
                                        ? originalAssignment.selected_departments
                                        : [...new Set(
                                            (originalAssignment?.job_quotes || [])
                                                .filter(q => !q.delivery_count || q.delivery_count === 0)
                                                .map(q => q.quote_type)
                                                .filter(Boolean)
                                        )];
                                    return depts.map(dept => (
                                        <option key={dept} value={dept}>
                                            {dept.charAt(0).toUpperCase() + dept.slice(1)}
                                        </option>
                                    ));
                                })()}
                            </select>
                        </div>
                        {/* Child display ID */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Child ID
                            </label>
                            <input
                                type="text"
                                placeholder={`e.g. ${originalAssignment.unique_id}R2`}
                                value={newChildRow.childUniqueId}
                                onChange={e => setNewChildRow(prev => ({ ...prev, childUniqueId: e.target.value }))}
                                className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 w-36"
                            />
                        </div>
                        {/* Expected finish date */}
                        {/* <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Finish Date
                            </label>
                            <input
                                type="date"
                                value={newChildRow.expectedFinishDate}
                                onChange={e => setNewChildRow(prev => ({ ...prev, expectedFinishDate: e.target.value }))}
                                className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400"
                            />
                        </div> */}
                        {/* Expected delivery date (not shown for Estimation jobs) */}
                        {formData.jobType !== "Estimation" && (
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Delivery Date
                                </label>
                                <input
                                    type="date"
                                    value={newChildRow.expectedDeliveryDate}
                                    onChange={e => setNewChildRow(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                                    className="text-xs border border-gray-300 dark:border-zinc-600 rounded-lg px-1 py-1.5 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 pl-8"
                                />
                            </div>
                        )}
                        {/* Add + Cancel buttons — aligned to bottom of row */}
                        <div className="flex items-center gap-2 pb-0.5">
                            <button
                                type="button"
                                disabled={!newChildRow.childUniqueId.trim() || !newChildRow.quoteType || newChildAddLoading}
                                onClick={handleAddChildDept}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
                            >
                                {newChildAddLoading
                                    ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <FiPlus className="w-3.5 h-3.5" />
                                }
                                Add
                            </button>
                            <button
                                type="button"
                                onClick={() => setNewChildRow(null)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                            >
                                <FiX className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Existing child department rows */}
                {(() => {
                    const sorted = [...childDepts].sort((a, b) => {
                        const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                        if (typeOrder !== 0) return typeOrder;
                        return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                    });
                    const activeDepts = sorted.filter(c => c.is_required);
                    // Only show disabled rows that were explicitly added as child departments
                    // (have a child_unique_id). Default old-system quotes that are simply
                    // not required are hidden entirely to avoid noise.
                    const disabledDepts = sorted.filter(c => !c.is_required && c.child_unique_id);

                    const renderChildRow = (child) => {
                        const isEditingThis = editingChildId === child.id;
                        return (
                            <div
                                key={child.id}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${child.is_required
                                    ? 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800'
                                    : 'border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900'
                                    }`}
                            >
                                <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-white ${child.is_required ? 'bg-gray-600' : 'bg-gray-300 dark:bg-zinc-600'
                                    }`}>
                                    {getChildQuoteIcon(child.quote_type)}
                                </div>

                                {/* ID display / inline editor */}
                                <div className={`flex-1 min-w-0 ${!child.is_required ? 'opacity-50' : ''}`}>
                                    {isEditingThis ? (
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5">
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    value={editingChildUniqueId}
                                                    onChange={e => { setEditingChildUniqueId(e.target.value); setChildIdError(''); }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleSaveChildUniqueId(child.id);
                                                        if (e.key === 'Escape') setEditingChildId(null);
                                                    }}
                                                    className="text-xs border border-gray-300 dark:border-zinc-500 rounded px-2 py-1 bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400 w-32"
                                                />
                                                <button
                                                    type="button"
                                                    disabled={childIdSaving || !editingChildUniqueId.trim()}
                                                    onClick={() => handleSaveChildUniqueId(child.id)}
                                                    className="p-1 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-40 transition-colors"
                                                >
                                                    {childIdSaving
                                                        ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        : <FiCheck className="w-3.5 h-3.5" />
                                                    }
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setEditingChildId(null); setChildIdError(''); }}
                                                    className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            {childIdError && (
                                                <p className="text-[10px] text-red-500 leading-none">{childIdError}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5 group/id">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-none capitalize">
                                                    {child.child_unique_id || child.quote_type}
                                                </p>
                                                {child.child_unique_id && (
                                                    <p className="text-xs text-gray-400 capitalize mt-0.5">{child.quote_type}</p>
                                                )}
                                            </div>
                                            {child.child_unique_id && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setEditingChildId(child.id);
                                                        setEditingChildUniqueId(child.child_unique_id || '');
                                                        setChildIdError('');
                                                    }}
                                                    className="opacity-0 group-hover/id:opacity-100 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-all"
                                                >
                                                    <FiEdit2 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className={`hidden sm:flex items-center gap-1 ${!child.is_required ? 'opacity-50' : ''}`}>
                                    {getDesignStatusBadge(child.design_status)}
                                    {child.quote_status && child.quote_status !== child.design_status && getDesignStatusBadge(child.quote_status)}
                                </div>

                                {child.expected_finish_date && (
                                    <span className={`hidden sm:flex items-center gap-1 text-[10px] text-gray-500 ${!child.is_required ? 'opacity-50' : ''}`}>
                                        <FiCalendar className="w-3 h-3" />
                                        {new Date(child.expected_finish_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                    </span>
                                )}

                                <button
                                    type="button"
                                    disabled={childActionLoading === child.id}
                                    onClick={() => handleToggleChildRequired(child.id, child.is_required)}
                                    className={`text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors flex-shrink-0 flex items-center gap-1 ${child.is_required
                                        ? 'border-gray-200 dark:border-zinc-600 text-gray-500 hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        : 'border-gray-200 dark:border-zinc-600 text-gray-500 hover:border-green-300 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                        }`}
                                >
                                    {childActionLoading === child.id ? (
                                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        child.is_required ? 'Disable' : 'Enable'
                                    )}
                                </button>
                            </div>
                        );
                    };

                    return (
                        <>
                            {activeDepts.length === 0 && newChildRow === null && (
                                <p className="text-xs text-gray-400 italic py-2">No child departments yet. Click "Add Department" to create one.</p>
                            )}

                            {/* Active rows */}
                            {activeDepts.length > 0 && (
                                <div className="space-y-1.5">
                                    {(showAllChildren ? activeDepts : activeDepts.slice(0, 5)).map(renderChildRow)}
                                    {activeDepts.length > 5 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllChildren(prev => !prev)}
                                            className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 transition-colors"
                                        >
                                            {showAllChildren ? `Show less` : `Show all (${activeDepts.length})`}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Disabled accordion */}
                            {disabledDepts.length > 0 && (
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setDisabledChildrenOpen(prev => !prev)}
                                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors group"
                                    >
                                        {disabledChildrenOpen
                                            ? <FiChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                            : <FiChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        }
                                        <span className="text-xs font-medium text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                                            Disabled
                                        </span>
                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-gray-100 dark:bg-zinc-700 text-gray-400 rounded-full leading-none">
                                            {disabledDepts.length}
                                        </span>
                                    </button>

                                    {disabledChildrenOpen && (
                                        <div className="space-y-1.5 mt-1.5">
                                            {disabledDepts.map(renderChildRow)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>
            {/* )} */}

            {/* Timeline */}
            {!isTimberJob && formData.jobType !== "Design" &&
                <InfoCard title="Timeline" icon={FiCalendar}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Expected Estimation Finish Date
                        </label>
                        <div className="relative">
                            <input
                                className="w-full pl-9 pr-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                type="date"
                                name="expectedEstimationFinishDate"
                                value={
                                    formData.expectedEstimationFinishDate
                                        ? formData.expectedEstimationFinishDate.split("T")[0]
                                        : ""
                                }
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                </InfoCard>
            }

            {isTimberJob &&
                <CheckboxButton
                    label="Invoiced"
                    isChecked={formData?.quotes?.timber?.invoiced || false}
                    onClick={(e) => handleNestedChange("quotes.timber.invoiced", !formData?.quotes?.timber?.invoiced)}
                />
            }

            {/* Job Specifications */}
            {!isTimberJob && (formData.quotes?.roof?.isRequired || formData.quotes?.wall?.isRequired || formData.quotes?.floor?.isRequired) && (
                <DropdownSection
                    title="Job Specifications"
                    titleStyle="text-zinc-700 text-sm"
                    icon={Info}
                    iconStyle="text-zinc-800"
                    subtext={``}
                    subtextStyle=""
                    defaultOpen={false}
                >
                    <div className="space-y-6">
                        {/* Roof Specifications */}
                        {formData.quotes?.roof?.isRequired && (
                            <InfoCard title="Roof Specifications" icon={FiHome}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Roof Type *
                                    </label>
                                    <div className="flex gap-2 sm:flex-row flex-col">
                                        <SelectionButton
                                            label="Tiled"
                                            isSelected={formData.roofType === "Tiled"}
                                            onClick={() => {
                                                if (formData.roofType === "Tiled") {
                                                    setFormData(prev => ({ ...prev, roofType: "", roofTrussCentres: "" }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, roofType: "Tiled", roofTrussCentres: "600" }));
                                                }
                                            }}
                                        />
                                        <SelectionButton
                                            label="Colorbond"
                                            isSelected={formData.roofType === "Colorbond"}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                roofType: prev.roofType === "Colorbond" ? "" : "Colorbond"
                                            }))}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Roof Truss Centres *
                                    </label>
                                    <div className="flex gap-2 flex-wrap">
                                        <SelectionButton
                                            label="600"
                                            isSelected={formData.roofTrussCentres === "600"}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                roofTrussCentres: prev.roofTrussCentres === "600" ? "" : "600"
                                            }))}
                                        />
                                        {formData.roofType !== "tiled" && (
                                            <SelectionButton
                                                label="900"
                                                isSelected={formData.roofTrussCentres === "900"}
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    roofTrussCentres: prev.roofTrussCentres === "900" ? "" : "900"
                                                }))}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Estimation Date Roof */}
                                {/* <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                <div className="flex gap-2 flex-wrap">
                                    <div className="relative w-full">
                                        <input
                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            type="date"
                                            name="estimationFinishDate"
                                            value={formData?.quotes?.roof?.expectedFinishDate || ""}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleNestedChange("quotes.roof.expectedFinishDate", val);
                                                if (val && !formData.quotes.roof?.expectedDeliveryDate) {
                                                    handleNestedChange("quotes.roof.expectedDeliveryDate", val);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div> */}

                                {/* Expected Delivery Date Roof */}
                                {/* {formData.jobType !== "Estimation" &&
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                        <div className="flex gap-2 flex-wrap">
                                            <div className="relative w-full">
                                                <input
                                                    className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.roof?.expectedDeliveryDate || ""}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        handleNestedChange("quotes.roof.expectedDeliveryDate", val);
                                                        if (val && !formData.quotes.roof?.expectedFinishDate) {
                                                            handleNestedChange("quotes.roof.expectedFinishDate", val);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                } */}

                                {/* Invoiced */}
                                {/* <CheckboxButton
                                    label="Invoiced"
                                    isChecked={formData?.quotes?.roof?.invoiced || false}
                                    onClick={(e) => handleNestedChange("quotes.roof.invoiced", !formData?.quotes?.roof?.invoiced)}
                                /> */}
                            </InfoCard>


                        )}

                        {/* Wall Specifications */}
                        {formData.quotes?.wall?.isRequired && (
                            <InfoCard title="Wall Specifications" icon={FiLayers}>
                                <div className="flex gap-2 sm:flex-row flex-col mb-8">
                                    <SelectionButton
                                        label="Single Storey"
                                        isSelected={formData.isSingleStorey === true}
                                        onClick={() => {
                                            const willBeSingle = formData.isSingleStorey !== true;
                                            setFormData(prev => ({
                                                ...prev,
                                                isSingleStorey: prev.isSingleStorey === true ? null : true,
                                                isTripleStorey: false,
                                                // quotes: willBeSingle ? {
                                                //     ...prev.quotes,
                                                //     floor: { ...prev.quotes.floor, isRequired: false }
                                                // } : prev.quotes
                                            }));
                                        }}
                                    />
                                    <SelectionButton
                                        label="Double Storey"
                                        isSelected={formData.isSingleStorey === false && !formData.isTripleStorey}
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            isSingleStorey: prev.isSingleStorey === false && !prev.isTripleStorey ? null : false,
                                            isTripleStorey: false
                                        }))}
                                    />
                                    <SelectionButton
                                        label="Triple Storey"
                                        isSelected={formData.isSingleStorey === false && formData.isTripleStorey === true}
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            isSingleStorey: false,
                                            isTripleStorey: !(prev.isSingleStorey === false && prev.isTripleStorey === true)
                                        }))}
                                    />
                                </div>

                                {formData.isSingleStorey === true ? (
                                    renderWallSpecs('singleStorey', formData.singleStorey, 'Single Storey Specifications')
                                ) : formData.isSingleStorey === false && formData.isTripleStorey ? (
                                    <div className="space-y-4 mt-2">
                                        {renderWallSpecs('tripleStorey.groundFloor', formData.tripleStorey.groundFloor, 'Ground Floor Specifications')}
                                        {renderWallSpecs('tripleStorey.firstFloor', formData.tripleStorey.firstFloor, 'First Floor Specifications')}
                                        {renderWallSpecs('tripleStorey.secondFloor', formData.tripleStorey.secondFloor, 'Second Floor Specifications')}
                                    </div>
                                ) : formData.isSingleStorey === false ? (
                                    <div className="space-y-4 mt-2">
                                        {renderWallSpecs('doubleStorey.groundFloor', formData.doubleStorey.groundFloor, 'Ground Floor Specifications')}
                                        {renderWallSpecs('doubleStorey.firstFloor', formData.doubleStorey.firstFloor, 'First Floor Specifications')}
                                    </div>
                                ) : null}


                                {/* Estimation Date wall */}

                                {/* <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                <div className="flex gap-2 flex-wrap">
                                    <div className="relative w-full">
                                        <input
                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            type="date"
                                            name="estimationFinishDate"
                                            value={formData?.quotes?.wall?.expectedFinishDate || ""}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleNestedChange("quotes.wall.expectedFinishDate", val);
                                                if (val && !formData.quotes.wall?.expectedDeliveryDate) {
                                                    handleNestedChange("quotes.wall.expectedDeliveryDate", val);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div> */}

                                {/* Expected Delivery Date Roof */}
                                {/* {formData.jobType !== "Estimation" &&
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                        <div className="flex gap-2 flex-wrap">
                                            <div className="relative w-full">
                                                <input
                                                    className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.wall?.expectedDeliveryDate || ""}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        handleNestedChange("quotes.wall.expectedDeliveryDate", val);
                                                        if (val && !formData.quotes.wall?.expectedFinishDate) {
                                                            handleNestedChange("quotes.wall.expectedFinishDate", val);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                } */}

                                {/* Invoiced */}
                                {/* <CheckboxButton
                                    label="Invoiced"
                                    isChecked={formData?.quotes?.wall?.invoiced || false}
                                    onClick={(e) => handleNestedChange("quotes.wall.invoiced", !formData?.quotes?.wall?.invoiced)}
                                /> */}
                            </InfoCard>
                        )}

                        {/* Floor Specifications */}
                        {formData.quotes?.floor?.isRequired && (
                            <InfoCard title="Floor Specifications" icon={FiLayers}>
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Floor Details</span>
                                    <button
                                        type="button"
                                        onClick={addFloor}
                                        className="flex items-center gap-2 px-3 py-1 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm"
                                    >
                                        <FiPlus className="w-4 h-4" />
                                        Add Floor
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {formData.floors.map((floor, index) => (
                                        <div key={index} className="border-l-2 border-gray-200 dark:border-zinc-600 pl-4 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-medium text-green-900 dark:text-green-300">
                                                    Floor {floor.floorNumber}
                                                </p>
                                                {formData.floors.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFloor(index)}
                                                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Floor Truss Size *
                                                    </label>
                                                    <div className="flex flex-col gap-2">

                                                        <select
                                                            value={floor.floorTrussSize}
                                                            onChange={(e) => handleFloorChange(index, 'floorTrussSize', e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors[`floor${index}TrussSize`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        >
                                                            <option value="" disabled>Select truss size</option>
                                                            <option value="310">310mm</option>
                                                            <option value="370">370mm</option>
                                                            <option value="420">420mm</option>
                                                            <option value="Longreach">Longreach</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Flooring Type *
                                                    </label>
                                                    <div className="flex flex-col gap-2">
                                                        <select
                                                            value={floor.flooring}
                                                            onChange={(e) => handleFloorChange(index, 'flooring', e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors[`floor${index}Flooring`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        >
                                                            <option value="" disabled>Select flooring</option>
                                                            <option value="Yellow tongue">Yellow tongue</option>
                                                            <option value="Compression sheet">Compression sheet</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="flex items-center pt-6">
                                                    <CheckboxButton
                                                        label="Balcony Included"
                                                        isChecked={floor.balconyIncluded}
                                                        onClick={() => {
                                                            const newFloors = [...formData.floors];
                                                            newFloors[index].balconyIncluded = !newFloors[index].balconyIncluded;
                                                            setFormData(prev => ({ ...prev, floors: newFloors }));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>


                                {/* Estimation Date floor */}
                                {/* <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                <div className="flex gap-2 flex-wrap">
                                    <div className="relative w-full">
                                        <input
                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            type="date"
                                            name="estimationFinishDate"
                                            value={formData?.quotes?.floor?.expectedFinishDate || ""}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleNestedChange("quotes.floor.expectedFinishDate", val);
                                                if (val && !formData.quotes.floor?.expectedDeliveryDate) {
                                                    handleNestedChange("quotes.floor.expectedDeliveryDate", val);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div> */}

                                {/* Expected Delivery Date Floor */}
                                {/* {formData.jobType !== "Estimation" &&
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                        <div className="flex gap-2 flex-wrap">
                                            <div className="relative w-full">
                                                <input
                                                    className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="date"
                                                    value={formData?.quotes?.floor?.expectedDeliveryDate || ""}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        handleNestedChange("quotes.floor.expectedDeliveryDate", val);
                                                        if (val && !formData.quotes.floor?.expectedFinishDate) {
                                                            handleNestedChange("quotes.floor.expectedFinishDate", val);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                } */}

                                {/* Invoiced */}
                                {/* <CheckboxButton
                                    label="Invoiced"
                                    isChecked={formData?.quotes?.floor?.invoiced || false}
                                    onClick={(e) => handleNestedChange("quotes.floor.invoiced", !formData?.quotes?.floor?.invoiced)}
                                /> */}
                            </InfoCard>
                        )}
                    </div>
                </DropdownSection>
            )}


            <DropdownSection
                title="Site Measure"
                titleStyle="text-zinc-700 text-sm"
                icon={TbMeterSquare}
                iconStyle="text-zinc-800"
                subtext={``}
                subtextStyle=""
                defaultOpen={false}
            >


                <CheckboxButton
                    label="Site Measure Required"
                    className="mb-2"
                    isChecked={formData.siteMeasureRequired}
                    onClick={async () => {
                        const oldValue = !!formData.siteMeasureRequired;
                        const newValue = !oldValue;

                        setFormData(prev => ({
                            ...prev,
                            siteMeasureRequired: newValue,
                        }));

                        try {
                            await updateAssignment(
                                originalAssignment.id,
                                { site_measure_required: newValue },
                                company.id
                            );

                            createJobActionLog({
                                job_id: originalAssignment.id,
                                company_id: company.id,
                                user_id: userProfile?.id,
                                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                                user_role: userProfile?.role,
                                action: 'site_measure_toggle',
                                details: `Site Measure Required turned ${newValue ? 'on' : 'off'}`,
                                changed_fields: ['site_measure_required'],
                                old_values: { site_measure_required: oldValue },
                                new_values: { site_measure_required: newValue },
                                action_origin: 'edit-assignment',
                            }).catch(() => { });

                        } catch (err) {
                            console.error('Failed to update site measure required:', err);

                            setFormData(prev => ({
                                ...prev,
                                siteMeasureRequired: oldValue,
                            }));

                            alert('Failed to update site measure required. Please try again.');
                        }
                    }}
                />

                {formData.siteMeasureRequired && (
                    <div className="space-y-3">
                        {/* Quote type toggles — instant save + log */}
                        <div className="flex gap-2 sm:flex-row flex-col">
                            {formData.quotes.wall?.isRequired && (
                                <CheckboxButton
                                    label="Wall"
                                    isChecked={formData.siteMeasureWall}
                                    onClick={() => handleSiteMeasureToggle('siteMeasureWall')}
                                />
                            )}

                            {formData.quotes.floor?.isRequired && (
                                <CheckboxButton
                                    label="Floor"
                                    isChecked={formData.siteMeasureFloor}
                                    onClick={() => handleSiteMeasureToggle('siteMeasureFloor')}
                                />
                            )}

                            {formData.quotes.roof?.isRequired && (
                                <CheckboxButton
                                    label="Truss"
                                    isChecked={formData.siteMeasureTruss}
                                    onClick={() => handleSiteMeasureToggle('siteMeasureTruss')}
                                />
                            )}
                        </div>

                        {/* Scheduled dates table — shown for each enabled type */}
                        {(() => {
                            const TYPE_MAP = [
                                { enabled: formData.siteMeasureWall, type: 'wall', key: 'siteMeasureWall', label: 'Wall' },
                                { enabled: formData.siteMeasureFloor, type: 'floor', key: 'siteMeasureFloor', label: 'Floor' },
                                { enabled: formData.siteMeasureTruss, type: 'roof', key: 'siteMeasureTruss', label: 'Truss' },
                            ];
                            const tableRows = TYPE_MAP.filter(t => t.enabled).flatMap(t =>
                                childDepts
                                    .filter(q => q.quote_type === t.type && q.is_required)
                                    .map(q => ({ ...t, quote: q }))
                            );
                            if (!tableRows.length) return null;
                            return (
                                <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                                    {/* Column headers */}
                                    <div className="bg-gray-50 dark:bg-zinc-900/60 px-4 py-2 border-b border-gray-200 dark:border-zinc-700 grid grid-cols-[1fr_160px_72px] gap-3">
                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Quote</span>
                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Scheduled Date</span>
                                        <div />
                                    </div>
                                    {tableRows.map(({ label, key, quote: q }) => (
                                        <div key={q.id} className="grid grid-cols-[1fr_160px_72px] gap-3 items-center px-4 py-3 border-b border-gray-100 dark:border-zinc-700/50 last:border-b-0 bg-white dark:bg-zinc-800">
                                            <div>
                                                <span className="text-sm font-semibold text-gray-800 dark:text-white">
                                                    {q.child_unique_id || originalAssignment?.unique_id || '—'}
                                                </span>
                                                <span className="text-xs text-gray-400 ml-1.5">({label})</span>
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                {q.site_measure_date
                                                    ? new Date(q.site_measure_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                                                    : <span className="text-gray-300 dark:text-zinc-600 italic text-xs">No date set</span>
                                                }
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setSmPopup({
                                                    quoteType: q.quote_type,
                                                    formKey: key,
                                                    quoteId: q.id,
                                                    label: q.child_unique_id || originalAssignment?.unique_id || '',
                                                    date: q.site_measure_date || '',
                                                    isEdit: true,
                                                })}
                                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-zinc-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                                            >
                                                <FiEdit2 className="w-3 h-3" />
                                                Edit
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                )}

            </DropdownSection>

            {/* Additional Information */}
            {!isTimberJob && <div className="space-y-6">
                <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-zinc-700 pb-2">
                    Additional Information
                </h2>

                <div className="space-y-4">
                    <CheckboxButton
                        label="Is this a unit?"
                        isChecked={formData.isUnit}
                        onClick={() => setFormData(prev => ({ ...prev, isUnit: !prev.isUnit }))}
                    />

                    {formData.isUnit && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Number of Units
                            </label>
                            <input
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                type="number"
                                value={formData.unitCount}
                                onChange={(e) => setFormData(prev => ({ ...prev, unitCount: e.target.value }))}
                                placeholder="How many units?"
                            />
                        </div>
                    )}


                </div>
            </div>}

            {/* File Management */}
            <div className="space-y-6">
                <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-zinc-700 pb-2">
                    Files & Documents
                </h2>

                <FileSearchBar
                    value={fileSearch}
                    onChange={setFileSearch}
                />

                {/* Existing Files */}
                {originalAssignment.job_files && originalAssignment.job_files.length > 0 && (
                    <div className="space-y-2">
                        {visibleExistingFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-700">
                                <div className="flex items-center gap-3">
                                    {getFileIcon(file.filename)}
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            <span
                                                className={`px-2 py-0.5 text-xs rounded-full mr-2 text-white 
                                                            ${file?.file_type === "initial" && "bg-blue-600"} 
                                                            ${file?.file_type === "design" && "bg-green-600"} 
                                                            ${file?.file_type === "estimation" && "bg-purple-600"} 
                                                            ${file?.file_type === "timber" && "bg-yellow-600"}
                                                         ${file?.file_type === "custom" && "bg-blue-600"}`
                                                }
                                            >
                                                {file?.file_type === "initial" ? "Job File" : capitalizeFirstLetter(file?.file_type)}
                                            </span>
                                            {file?.quote_type &&
                                                <span
                                                    className={`px-2 py-0.5 text-xs rounded-full mr-2 text-white bg-green-700`}
                                                >
                                                    {capitalizeFirstLetter(file?.quote_type)}
                                                </span>
                                            }
                                            {file.original_name}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Uploaded by {file.uploaded_by_name} • {file.filename}
                                            {new Date(file.uploaded_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => markFileForDeletion(file.id)}
                                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    title="Mark for deletion"
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {visibleExistingFiles.length <= 0 && (
                            <p className="text-sm m-0 text-zinc-400 italic">
                                {hasFileSearch
                                    ? "No files found for this search"
                                    : "This job does not have any files uploaded"}
                            </p>
                        )}
                    </div>
                )}

                {/* Files Marked for Deletion */}
                {filesToDelete.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-lg font-medium text-red-700 dark:text-red-400">Files to be Deleted</h3>
                        <div className="space-y-2">
                            {originalAssignment.job_files
                                .filter(file => filesToDelete.includes(file.id))
                                .map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                        <div className="flex items-center gap-3">
                                            <FiFileText className="w-5 h-5 text-red-400" />
                                            <div>
                                                <p className="text-sm font-medium text-red-900 dark:text-red-300 line-through">{file.original_name}</p>
                                                <p className="text-xs text-red-600 dark:text-red-400">Will be deleted on save</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => unmarkFileForDeletion(file.id)}
                                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                            title="Restore file"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-700">Upload New Files</h3>
                    <DragDropUploader
                        onFilesChange={handlePdfsChange}
                        clearFiles={clearPdfs}
                        showFileOptions={true}
                        availableCategories={getAvailableCategories()}
                        availableQuoteTypes={getAvailableDepartments()}
                    />
                </div>

                {/* New Files Preview */}
                {/* {newFiles.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-medium text-green-700 dark:text-green-400">New Files to Upload</h3>
                                <div className="space-y-2">
                                    {newFiles.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                            <div className="flex items-center gap-3">
                                                <FiFileText className="w-5 h-5 text-green-500" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{file.name}</p>
                                                    <p className="text-xs text-green-600 dark:text-green-400">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB • Will be uploaded on save
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeNewFile(index)}
                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Remove file"
                                            >
                                                <FiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )} */}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-zinc-700">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
                >
                    {isSubmitting ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Saving...
                        </>
                    ) : (
                        <>
                            <FiSave className="w-4 h-4" />
                            Save Changes
                        </>
                    )}
                </button>

                <button
                    type="button"
                    onClick={() => isModal ? onClose() : navigate(`/${companySlug}/assignment/${jobId}`, {
                        state: { from: location.pathname }
                    })}
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-800 transition-colors font-medium"
                >
                    Cancel
                </button>
            </div>

            {/* Validation Errors Summary */}
            {Object.keys(errors).length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <FiAlertCircle className="w-5 h-5 text-red-500" />
                        <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Please fix the following errors:</h3>
                    </div>
                    <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 space-y-1">
                        {Object.values(errors).map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

        </form>
    );

    // ── Site measure date popup ───────────────────────────────────────────────
    const smPopupJsx = smPopup ? (
        <div className="mobile-dialog-shell fixed inset-0 !mt-0 z-[400] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-zinc-700 w-full max-w-sm mx-4">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 dark:border-zinc-700">
                    <p className="text-base font-bold text-gray-900 dark:text-white">
                        {smPopup.label}
                        <span className="text-gray-400 dark:text-zinc-500 font-normal text-sm ml-1.5 capitalize">
                            ({smPopup.quoteType === 'roof' ? 'Truss' : smPopup.quoteType})
                        </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Site Measure</p>
                </div>

                {/* Date picker */}
                <div className="px-5 py-5">
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        value={smPopup.date}
                        onChange={e => setSmPopup(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-3 pl-8 py-2.5 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    />
                </div>

                {/* Actions */}
                <div className="px-5 py-4 border-t border-gray-100 dark:border-zinc-700 flex items-center justify-between gap-2">
                    {/* Clear date — only shown when a date is already set */}
                    <div>
                        {smPopup.date && (
                            <button
                                type="button"
                                onClick={handleSmClearDate}
                                disabled={smSaving}
                                className="px-3 py-2 text-xs font-medium text-red-500 border border-red-200 dark:border-red-800/50 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                            >
                                {smSaving ? (
                                    <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <FiX className="w-3 h-3" />
                                )}
                                Clear Date
                            </button>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setSmPopup(null)}
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-zinc-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSmPopupSubmit}
                            disabled={!smPopup.date || smSaving}
                            className="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {smSaving && (
                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            {smPopup.isEdit ? 'Update' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    ) : null;

    if (isModal) {
        return (
            <>
                <div className="mobile-dialog-shell fixed inset-0 top-0 !mt-0 z-[200] flex justify-end py-4 px-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
                    {/* Slide-in panel */}
                    <div className="relative rounded-lg w-full max-w-3xl h-full overflow-y-auto bg-white dark:bg-zinc-900 shadow-2xl p-6 sm:p-8">
                        {formContent}
                    </div>
                </div>
                {smPopupJsx}


                <ConfirmationPopup
                    isOpen={!!disableChildPopup}
                    title="Disable last department?"
                    message={
                        disableChildPopup
                            ? `This is the only active department for this job. Disabling #${disableChildPopup.childLabel} will remove the job from Job Editor and other schedules.\n\nThe job will still be searchable on the Jobs page.\n\nAre you sure you want to continue?`
                            : ""
                    }
                    confirmText="Disable Department"
                    cancelText="Cancel"
                    onConfirm={handleConfirmDisableLastChild}
                    onCancel={handleCancelDisableLastChild}
                />

                <ConfirmationPopup
                    isOpen={!!quoteDateWarningPopup}
                    title="This may break the delivery order"
                    message={
                        quoteDateWarningPopup ? (() => {
                            const warnings = quoteDateWarningPopup.warnings || [];
                            const visibleWarnings = showQuoteDateWarningDetails
                                ? warnings
                                : warnings.slice(0, 3);
                            const hiddenCount = Math.max(warnings.length - visibleWarnings.length, 0);

                            return (
                                <div className="space-y-4 text-sm text-gray-700">
                                    <p>
                                        Jobs are expected to be sent in this order: <strong>Wall, Floor, then Roof</strong>.
                                        This ETA date may conflict with other department dates.
                                    </p>

                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 max-h-[260px] overflow-y-auto">
                                        <div className="flex items-start gap-2">
                                            <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                            <div className="min-w-0 flex-1 text-start">
                                                <p className="font-semibold">
                                                    Conflicting department dates
                                                </p>

                                                <div className="mt-3 space-y-2">
                                                    {visibleWarnings.map(item => (
                                                        <div
                                                            key={item.id}
                                                            className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2 text-xs"
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="font-bold text-amber-950">
                                                                    {item.child_unique_id}
                                                                </span>
                                                                <span className="rounded-md bg-amber-100 px-2 py-0.5 font-semibold capitalize text-amber-800">
                                                                    {item.quote_type}
                                                                </span>
                                                            </div>

                                                            <p className="mt-1 text-amber-800">
                                                                {item.message}
                                                            </p>

                                                            <p className="mt-1 font-semibold text-amber-900">
                                                                Current date: {formatWarningDate(item.date)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {hiddenCount > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowQuoteDateWarningDetails(prev => !prev);
                                                        }}
                                                        className="mt-3 text-xs font-bold text-amber-900 underline"
                                                    >
                                                        {showQuoteDateWarningDetails
                                                            ? 'Show fewer warnings'
                                                            : `Show ${hiddenCount} more`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <p className="font-medium">
                                        Are you sure you want to save this ETA date?
                                    </p>
                                </div>
                            );
                        })() : ""
                    }
                    confirmText="Save ETA Date"
                    cancelText="Cancel"
                    onConfirm={handleConfirmQuoteDateWarning}
                    onCancel={handleCancelQuoteDateWarning}
                />
            </>
        );
    }

    return (
        <>
            {formContent}
            {smPopupJsx}

            <ConfirmationPopup
                isOpen={!!disableChildPopup}
                title="Disable last department?"
                message={
                    disableChildPopup
                        ? `This is the only active department for this job. Disabling #${disableChildPopup.childLabel} will remove the job from Job Editor and other schedules.\n\nThe job will still be searchable on the Jobs page.\n\nAre you sure you want to continue?`
                        : ""
                }
                confirmText="Disable Department"
                cancelText="Cancel"
                onConfirm={handleConfirmDisableLastChild}
                onCancel={handleCancelDisableLastChild}
            />

            <ConfirmationPopup
                isOpen={!!quoteDateWarningPopup}
                title="This may break the delivery order"
                message={
                    quoteDateWarningPopup ? (() => {
                        const warnings = quoteDateWarningPopup.warnings || [];
                        const visibleWarnings = showQuoteDateWarningDetails
                            ? warnings
                            : warnings.slice(0, 3);
                        const hiddenCount = Math.max(warnings.length - visibleWarnings.length, 0);

                        return (
                            <div className="space-y-4 text-sm text-gray-700">
                                <p>
                                    Jobs are expected to be sent in this order: <strong>Wall, Floor, then Roof</strong>.
                                    This ETA date may conflict with other department dates.
                                </p>

                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 max-h-[260px] overflow-y-auto">
                                    <div className="flex items-start gap-2">
                                        <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                        <div className="min-w-0 flex-1 text-start">
                                            <p className="font-semibold">
                                                Conflicting department dates
                                            </p>

                                            <div className="mt-3 space-y-2">
                                                {visibleWarnings.map(item => (
                                                    <div
                                                        key={item.id}
                                                        className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2 text-xs"
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="font-bold text-amber-950">
                                                                {item.child_unique_id}
                                                            </span>
                                                            <span className="rounded-md bg-amber-100 px-2 py-0.5 font-semibold capitalize text-amber-800">
                                                                {item.quote_type}
                                                            </span>
                                                        </div>

                                                        <p className="mt-1 text-amber-800">
                                                            {item.message}
                                                        </p>

                                                        <p className="mt-1 font-semibold text-amber-900">
                                                            Current date: {formatWarningDate(item.date)}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>

                                            {hiddenCount > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowQuoteDateWarningDetails(prev => !prev);
                                                    }}
                                                    className="mt-3 text-xs font-bold text-amber-900 underline"
                                                >
                                                    {showQuoteDateWarningDetails
                                                        ? 'Show fewer warnings'
                                                        : `Show ${hiddenCount} more`}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p className="font-medium">
                                    Are you sure you want to save this ETA date?
                                </p>
                            </div>
                        );
                    })() : ""
                }
                confirmText="Save ETA Date"
                cancelText="Cancel"
                onConfirm={handleConfirmQuoteDateWarning}
                onCancel={handleCancelQuoteDateWarning}
            />
        </>
    );
};

export default EditAssignment;


